import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import {
  AdminAPI,
  AdminAuditEvent,
  AdminConfig,
  AdminCrawlerSettings,
  AdminMetricsWeek,
  AdminOptionUniverseCategory,
  AdminOptionUniverseItem,
  AdminPromoCode,
  AdminPromoCodeCreate,
  AdminPromoCodeUpdate,
  AdminSubscriptionLedgerItem,
  AdminUOAOverview,
  AdminWebSubscription,
  PasskeyStatus,
  AdminUser,
  AdminUserDetail,
  CollectorEvent,
  CollectorHealth,
  hasStoredAdminToken,
  ProductEventName,
  SignupTrial,
  Summary,
} from './api'
import './styles.css'
import { SaveItem, SettingsPanel } from './admin_settings'

type Page = 'overview' | 'users' | 'subscriptions' | 'options' | 'uoa' | 'growth' | 'settings' | 'operations' | 'audit' | 'metrics'

type Status = {
  message: string
  kind: 'muted' | 'ok' | 'err'
}

type SessionState = 'checking' | 'signed_in' | 'signed_out'

type PageLoadOptions = {
  force?: boolean
  background?: boolean
}

type SignupTrialDraft = {
  enabled: boolean
  startAt: string
  endAt: string
  days: string
  productID: string
}

type UserDraft = {
  email: string
  status: string
  productID: string
  environment: string
  expiresAt: string
}

type EntitlementView = {
  isActive: boolean
  isTrial: boolean
  state: 'pro' | 'trial' | 'expired' | 'free'
  label: string
  productLabel: string
  productDetail: string
}

type UserActionView = {
  canGrant: boolean
  grantLabel: string
  canRevoke: boolean
  revokeLabel: string
  note: string
}

type ActionItem = {
  title: string
  body: string
  page: Page
  priority: 'high' | 'medium' | 'low'
}

type UserBehaviorEvent = {
  key: string
  title: string
  body: string
  at?: string
}

type UserFocus = {
  userID?: string
  installID?: string
  ip?: string
  txnID?: string
  source?: string
}

type UserDeviceFootprint = {
  key: string
  installID: string
  ips: string[]
  platforms: string[]
  environments: string[]
  sources: string[]
  txnIDs: string[]
  tokenCount: number
  restoreCount: number
  lastSeenAt?: string
}

type UserSubscriptionLink = {
  key: string
  txnID: string
  product: string
  status: string
  installID?: string
  ip?: string
  source: string
  expiresAt?: string
  lastSeenAt?: string
}

type SubscriptionFootprint = {
  key: string
  installID: string
  users: Array<{ id: string; label: string; authProvider?: string }>
  ips: string[]
  sources: string[]
  eventCount: number
  lastSeenAt?: string
}

type PageMeta = {
  id: Page
  label: string
  icon: string
  group: 'Command' | 'Customers' | 'Data' | 'System'
  title: string
  eyebrow: string
  description: string
}

const defaultTrialDraft: SignupTrialDraft = {
  enabled: false,
  startAt: '',
  endAt: '',
  days: '30',
  productID: 'promo.signup_trial',
}

function pageFromPath(pathname: string): Page {
  const slug = pathname.replace(/^\/+|\/+$/g, '')
  switch (slug) {
    case '':
    case 'overview':
      return 'overview'
    case 'users':
    case 'subscriptions':
    case 'options':
    case 'uoa':
    case 'growth':
    case 'settings':
    case 'operations':
    case 'audit':
    case 'metrics':
      return slug
    default:
      return 'overview'
  }
}

function pathForPage(page: Page): string {
  return page === 'overview' ? '/' : `/${page}`
}

function App() {
  const [apiBase, setAPIBase] = useState(sessionStorage.getItem('navoption.admin.apiBase') || 'https://api.navoption.com')
  const [sessionReady, setSessionReady] = useState(() => hasStoredAdminToken())
  const [sessionState, setSessionState] = useState<SessionState>(() => hasStoredAdminToken() ? 'signed_in' : 'checking')
  const [setupKey, setSetupKey] = useState('')
  const [passkeyStatus, setPasskeyStatus] = useState<PasskeyStatus | null>(null)
  const [page, setPage] = useState<Page>(() => pageFromPath(window.location.pathname))
  const [status, setStatus] = useState<Status>({ message: 'Checking admin session...', kind: 'muted' })
  const [loading, setLoading] = useState(false)

  const [summary, setSummary] = useState<Summary | null>(null)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [userQuery, setUserQuery] = useState('')
  const [userTier, setUserTier] = useState('')
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null)
  const [userDraft, setUserDraft] = useState<UserDraft | null>(null)
  const [userFocus, setUserFocus] = useState<UserFocus | null>(() => userFocusFromLocation())
  const [subscriptions, setSubscriptions] = useState<AdminSubscriptionLedgerItem[]>([])
  const [subscriptionQuery, setSubscriptionQuery] = useState('')
  const [subscriptionStatus, setSubscriptionStatus] = useState('')
  const [optionUniverse, setOptionUniverse] = useState<AdminOptionUniverseItem[]>([])
  const [optionQuery, setOptionQuery] = useState('')
  const [optionBulk, setOptionBulk] = useState('')
  const [optionCategories, setOptionCategories] = useState<AdminOptionUniverseCategory[]>([])
  const [crawlerSettings, setCrawlerSettings] = useState<AdminCrawlerSettings | null>(null)
  const [uoaOverview, setUoaOverview] = useState<AdminUOAOverview | null>(null)
  const [signupTrial, setSignupTrial] = useState<SignupTrial | null>(null)
  const [trialDraft, setTrialDraft] = useState<SignupTrialDraft>(defaultTrialDraft)
  const [promoCodes, setPromoCodes] = useState<AdminPromoCode[]>([])
  const [webSubscriptions, setWebSubscriptionsState] = useState<AdminWebSubscription[]>([])
  const [config, setConfig] = useState<AdminConfig | null>(null)
  const [events, setEvents] = useState<CollectorEvent[]>([])
  const [auditEvents, setAuditEvents] = useState<AdminAuditEvent[]>([])
  const [metricsWeeks, setMetricsWeeks] = useState<AdminMetricsWeek[]>([])

  const api = useMemo(() => new AdminAPI(apiBase), [apiBase])
  const isConnected = sessionReady
  const appliedUserFocusKey = useRef('')
  const loadedPages = useRef<Set<Page>>(new Set())
  const loadingPages = useRef<Set<Page>>(new Set())

  function navigatePage(next: Page, search?: URLSearchParams | string) {
    setPage(next)
    const rawSearch = typeof search === 'string' ? search : search?.toString()
    const nextURL = pathForPage(next) + (rawSearch ? '?' + rawSearch : '')
    if (window.location.pathname + window.location.search !== nextURL) {
      window.history.pushState({ page: next }, '', nextURL)
    }
  }

  useEffect(() => {
    const cleanBase = apiBase.replace(/\/+$/, '')
    const client = new AdminAPI(cleanBase)
    setAPIBase(cleanBase)
    sessionStorage.setItem('navoption.admin.apiBase', cleanBase)
    void restoreAdminSession(client)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const onPopState = () => {
      setPage(pageFromPath(window.location.pathname))
      setUserFocus(userFocusFromLocation())
      appliedUserFocusKey.current = ''
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    const focus = userFocus || userFocusFromLocation()
    const key = userFocusKey(focus)
    if (!sessionReady || page !== 'users' || !focus || !key || appliedUserFocusKey.current === key) return
    appliedUserFocusKey.current = key
    setUserFocus(focus)
    void applyUserFocus(focus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady, page, userFocus])

  useEffect(() => {
    if (sessionState !== 'signed_in') return
    void loadPageData(page, api)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState, page, api])

  async function refreshPasskeyStatus(client = new AdminAPI(apiBase.replace(/\/+$/, ''))) {
    try {
      setPasskeyStatus(await client.passkeyStatus())
    } catch (error) {
      setStatus({ message: errorMessage(error), kind: 'err' })
    }
  }

  async function restoreAdminSession(client = api) {
    setLoading(true)
    setStatus({ message: 'Checking admin session...', kind: 'muted' })
    let passkeyLoaded = false
    const passkeyStatusPromise = client.passkeyStatus()
      .then((next) => {
        passkeyLoaded = true
        setPasskeyStatus(next)
      })
      .catch(() => undefined)
    try {
      await client.session()
      setSessionReady(true)
      setSessionState('signed_in')
      setStatus({ message: 'Live session restored.', kind: 'ok' })
      setLoading(false)
      void loadRuntimeData(client, { background: true })
    } catch (error) {
      await passkeyStatusPromise
      if (!passkeyLoaded) {
        void refreshPasskeyStatus(client)
      }
      setSummary(null)
      setSessionReady(false)
      setSessionState('signed_out')
      const message = errorMessage(error)
      setStatus({
        message: isSessionError(message) ? 'Passkey required.' : message,
        kind: isSessionError(message) ? 'muted' : 'err',
      })
      setLoading(false)
    }
  }

  async function registerPasskey(event?: FormEvent) {
    event?.preventDefault()
    const cleanBase = apiBase.replace(/\/+$/, '')
    setAPIBase(cleanBase)
    sessionStorage.setItem('navoption.admin.apiBase', cleanBase)
    setLoading(true)
    setStatus({ message: 'Creating passkey...', kind: 'muted' })
    try {
      const client = new AdminAPI(cleanBase)
      const options = await client.startPasskeyRegistration(setupKey.trim())
      const credentialNext = await navigator.credentials.create({ publicKey: creationOptionsFromJSON(options) })
      if (!credentialNext) throw new Error('Passkey creation was cancelled')
      const next = await client.finishPasskeyRegistration(publicKeyCredentialToJSON(credentialNext as PublicKeyCredential))
      setPasskeyStatus(next)
      setSetupKey('')
      setStatus({ message: 'Passkey ready. Sign in to open admin.', kind: 'ok' })
    } catch (error) {
      setStatus({ message: errorMessage(error), kind: 'err' })
    } finally {
      setLoading(false)
    }
  }

  async function signInPasskey(event?: FormEvent) {
    event?.preventDefault()
    const cleanBase = apiBase.replace(/\/+$/, '')
    setAPIBase(cleanBase)
    sessionStorage.setItem('navoption.admin.apiBase', cleanBase)
    setLoading(true)
    setStatus({ message: 'Checking passkey...', kind: 'muted' })
    try {
      const client = new AdminAPI(cleanBase)
      const options = await client.startPasskeyLogin()
      const credentialNext = await navigator.credentials.get({ publicKey: requestOptionsFromJSON(options) })
      if (!credentialNext) throw new Error('Passkey sign-in was cancelled')
      const login = await client.finishPasskeyLogin(publicKeyCredentialToJSON(credentialNext as PublicKeyCredential))
      loadedPages.current.clear()
      setSessionReady(true)
      setSessionState('signed_in')
      setStatus({ message: 'Admin session opened until ' + fmtDate(login.expires_at), kind: 'ok' })
      const clientNext = new AdminAPI(cleanBase)
      void loadRuntimeData(clientNext, { background: true })
      await loadPageData(page, clientNext, { force: true })
    } catch (error) {
      setStatus({ message: errorMessage(error), kind: 'err' })
    } finally {
      setLoading(false)
    }
  }

  async function signOut() {
    try {
      await api.logout()
    } catch {
      // Clearing local state still gives the operator a clean signed-out UI.
    }
    setSessionReady(false)
    setSessionState('signed_out')
    loadedPages.current.clear()
    setSummary(null)
    setStatus({ message: 'Signed out.', kind: 'muted' })
  }

  async function loadRuntimeData(client = api, options: PageLoadOptions = {}) {
    if (!options.background) setLoading(true)
    const [summaryRes, configRes] = await Promise.allSettled([
      client.summary(),
      client.config(),
    ])
    if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value)
    if (configRes.status === 'fulfilled') setConfig(configRes.value)
    const failed = [summaryRes, configRes].filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    failed.forEach((r) => console.warn('admin runtime load: endpoint failed', r.reason))
    if (handleSessionFailures(failed)) {
      if (!options.background) setLoading(false)
      return
    }
    if (!options.background) {
      setStatus({
        message: failed.length === 0 ? 'Runtime refreshed.' : `Runtime refreshed with ${failed.length} skipped endpoint${failed.length === 1 ? '' : 's'}.`,
        kind: failed.length === 0 ? 'ok' : 'err',
      })
      setLoading(false)
    }
  }

  async function loadPageData(target: Page = page, client = api, options: PageLoadOptions = {}) {
    if (!options.force && loadedPages.current.has(target)) return
    if (loadingPages.current.has(target)) return
    loadingPages.current.add(target)
    if (!options.background) {
      setLoading(true)
      setStatus({ message: `Loading ${pageMeta(target).label}...`, kind: 'muted' })
    }
    const failures: PromiseRejectedResult[] = []
    const collectFailures = (results: PromiseSettledResult<unknown>[]) => {
      failures.push(...results.filter((r): r is PromiseRejectedResult => r.status === 'rejected'))
    }
    try {
      switch (target) {
        case 'overview': {
          const [summaryRes, configRes, trialRes, eventsRes, auditRes] = await Promise.allSettled([
            client.summary(),
            client.config(),
            client.signupTrial(),
            client.events(),
            client.auditEvents(),
          ])
          if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value)
          if (configRes.status === 'fulfilled') setConfig(configRes.value)
          if (trialRes.status === 'fulfilled') {
            setSignupTrial(trialRes.value)
            setTrialDraft(makeTrialDraft(trialRes.value))
          }
          if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value)
          if (auditRes.status === 'fulfilled') setAuditEvents(auditRes.value)
          collectFailures([summaryRes, configRes, trialRes, eventsRes, auditRes])
          break
        }
        case 'users': {
          const results = await Promise.allSettled([client.users(userQuery, userTier)])
          const [usersRes] = results
          if (usersRes.status === 'fulfilled') setUsers(usersRes.value)
          collectFailures(results)
          break
        }
        case 'subscriptions': {
          const [subsRes, webSubsRes] = await Promise.allSettled([
            client.subscriptions(subscriptionQuery, subscriptionStatus),
            client.webSubscriptions(subscriptionQuery, subscriptionStatus),
          ])
          if (subsRes.status === 'fulfilled') setSubscriptions(subsRes.value)
          if (webSubsRes.status === 'fulfilled') setWebSubscriptionsState(webSubsRes.value)
          collectFailures([subsRes, webSubsRes])
          break
        }
        case 'options': {
          const [optionsRes, categoriesRes, crawlerRes] = await Promise.allSettled([
            client.optionUniverse(optionQuery),
            client.optionCategories(),
            client.crawlerSettings(),
          ])
          if (optionsRes.status === 'fulfilled') setOptionUniverse(optionsRes.value)
          if (categoriesRes.status === 'fulfilled') setOptionCategories(categoriesRes.value)
          if (crawlerRes.status === 'fulfilled') setCrawlerSettings(crawlerRes.value)
          collectFailures([optionsRes, categoriesRes, crawlerRes])
          break
        }
        case 'uoa': {
          const [uoaRes, configRes] = await Promise.allSettled([
            client.uoaOverview(),
            client.config(),
          ])
          if (uoaRes.status === 'fulfilled') setUoaOverview(uoaRes.value)
          if (configRes.status === 'fulfilled') setConfig(configRes.value)
          collectFailures([uoaRes, configRes])
          break
        }
        case 'growth': {
          const [trialRes, promoRes] = await Promise.allSettled([
            client.signupTrial(),
            client.promoCodes(),
          ])
          if (trialRes.status === 'fulfilled') {
            setSignupTrial(trialRes.value)
            setTrialDraft(makeTrialDraft(trialRes.value))
          }
          if (promoRes.status === 'fulfilled') setPromoCodes(promoRes.value)
          collectFailures([trialRes, promoRes])
          break
        }
        case 'settings': {
          const results = await Promise.allSettled([client.config()])
          const [configRes] = results
          if (configRes.status === 'fulfilled') setConfig(configRes.value)
          collectFailures(results)
          break
        }
        case 'operations': {
          const [summaryRes, configRes, eventsRes] = await Promise.allSettled([
            client.summary(),
            client.config(),
            client.events(),
          ])
          if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value)
          if (configRes.status === 'fulfilled') setConfig(configRes.value)
          if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value)
          collectFailures([summaryRes, configRes, eventsRes])
          break
        }
        case 'audit': {
          const results = await Promise.allSettled([client.auditEvents()])
          const [auditRes] = results
          if (auditRes.status === 'fulfilled') setAuditEvents(auditRes.value)
          collectFailures(results)
          break
        }
        case 'metrics': {
          const results = await Promise.allSettled([client.metrics()])
          const [metricsRes] = results
          if (metricsRes.status === 'fulfilled') setMetricsWeeks(metricsRes.value)
          collectFailures(results)
          break
        }
      }
      if (failures.length === 0) loadedPages.current.add(target)
      failures.forEach((r) => console.warn(`admin ${target} load: endpoint failed`, r.reason))
      if (handleSessionFailures(failures)) return
      if (!options.background) {
        setStatus({
          message: failures.length === 0
            ? `${pageMeta(target).label} loaded.`
            : `${pageMeta(target).label} loaded with ${failures.length} skipped endpoint${failures.length === 1 ? '' : 's'}.`,
          kind: failures.length === 0 ? 'ok' : 'err',
        })
      }
    } finally {
      loadingPages.current.delete(target)
      if (!options.background) setLoading(false)
    }
  }

  function handleSessionFailures(failures: PromiseRejectedResult[]) {
    if (!failures.some((failure) => isSessionError(errorMessage(failure.reason)))) return false
    loadedPages.current.clear()
    setSummary(null)
    setSessionReady(false)
    setSessionState('signed_out')
    setStatus({ message: 'Passkey required.', kind: 'muted' })
    return true
  }

  async function refreshCurrentPage() {
    await mutate('Page refreshed.', async () => {
      await Promise.all([
        loadRuntimeData(api, { background: true }),
        loadPageData(page, api, { force: true, background: true }),
      ])
    })
  }

  async function searchUsers(event?: FormEvent) {
    event?.preventDefault()
    await mutate('Users refreshed.', async () => {
      setUsers(await api.users(userQuery, userTier))
    })
  }

  async function searchSubscriptions(event?: FormEvent) {
    event?.preventDefault()
    await mutate('Subscriptions refreshed.', async () => {
      const [storeKit, web] = await Promise.all([
        api.subscriptions(subscriptionQuery, subscriptionStatus),
        api.webSubscriptions(subscriptionQuery, subscriptionStatus),
      ])
      setSubscriptions(storeKit)
      setWebSubscriptionsState(web)
    })
  }

  async function searchOptionUniverse(event?: FormEvent) {
    event?.preventDefault()
    await mutate('Option universe refreshed.', async () => {
      setOptionUniverse(await api.optionUniverse(optionQuery))
    })
  }

  async function saveOptionSymbols(categoryID?: number | null, event?: FormEvent) {
    event?.preventDefault()
    await mutate('Option universe updated.', async () => {
      const symbols = splitSymbols(optionBulk)
      const payload: { symbols: string[]; enabled: boolean; category_id?: number | null } = { symbols, enabled: true }
      if (categoryID !== undefined) payload.category_id = categoryID
      const universe = await api.upsertOptionSymbols(payload)
      setOptionUniverse(universe)
      if (categoryID !== undefined) setOptionCategories(await api.optionCategories())
      setOptionBulk('')
      setAuditEvents(await api.auditEvents())
    })
  }

  async function setOptionSymbolEnabled(item: AdminOptionUniverseItem, enabled: boolean) {
    await mutate(enabled ? 'Symbol enabled for option crawl.' : 'Symbol disabled for option crawl.', async () => {
      setOptionUniverse(await api.upsertOptionSymbols({
        symbol: item.symbol,
        enabled,
        priority: item.priority,
        notes: item.notes,
      }))
      setAuditEvents(await api.auditEvents())
    })
  }

  async function deleteOptionSymbol(symbol: string) {
    await mutate('Symbol removed from option universe.', async () => {
      await api.deleteOptionSymbol(symbol)
      setOptionUniverse(await api.optionUniverse(optionQuery))
      setAuditEvents(await api.auditEvents())
    })
  }

  async function moveSymbolsToCategory(symbols: string[], categoryID: number | null) {
    if (symbols.length === 0) return
    await mutate(`Moved ${symbols.length} symbol${symbols.length === 1 ? '' : 's'}.`, async () => {
      await api.bulkMoveOptionSymbols(symbols, categoryID)
      const moved = new Set(symbols.map((s) => s.toUpperCase()))
      const category = categoryID == null ? null : optionCategories.find((c) => c.id === categoryID) ?? null
      const updatedAt = new Date().toISOString()
      setOptionUniverse((current) => current.map((item) => (
        moved.has(item.symbol)
          ? {
              ...item,
              category_id: category?.id,
              category_slug: category?.slug,
              category_name: category?.name,
              updated_at: updatedAt,
            }
          : item
      )))
      const [categories, audit] = await Promise.all([
        api.optionCategories(),
        api.auditEvents(),
      ])
      setOptionCategories(categories)
      setAuditEvents(audit)
    })
  }

  async function createOptionCategory(name: string, description: string) {
    const trimmed = name.trim()
    if (!trimmed) return
    await mutate('Category created.', async () => {
      setOptionCategories(await api.createOptionCategory({ name: trimmed, description: description.trim() }))
      setAuditEvents(await api.auditEvents())
    })
  }

  async function renameOptionCategory(id: number, name: string) {
    const trimmed = name.trim()
    if (!trimmed) return
    await mutate('Category renamed.', async () => {
      setOptionCategories(await api.updateOptionCategory(id, { name: trimmed }))
      setOptionUniverse(await api.optionUniverse(optionQuery))
      setAuditEvents(await api.auditEvents())
    })
  }

  async function removeOptionCategory(id: number, reassignTo: number | null) {
    await mutate('Category deleted.', async () => {
      await api.deleteOptionCategory(id, reassignTo)
      const [categories, universe] = await Promise.all([
        api.optionCategories(),
        api.optionUniverse(optionQuery),
      ])
      setOptionCategories(categories)
      setOptionUniverse(universe)
      setAuditEvents(await api.auditEvents())
    })
  }

  async function saveCrawlerSettings(payload: Partial<Pick<AdminCrawlerSettings,
    'rate_per_min' | 'burst' | 'workers' | 'min_pass_gap_sec' | 'pass_timeout_sec'>>) {
    await mutate('Crawler settings updated; collector hot-reloaded.', async () => {
      setCrawlerSettings(await api.updateCrawlerSettings(payload))
      setAuditEvents(await api.auditEvents())
    })
  }

  async function triggerUOADetect() {
    await mutate('UOA detection requested. Refresh after the collector records the run.', async () => {
      await api.uoaDetectNow('admin-console')
      await sleep(1200)
      setUoaOverview(await api.uoaOverview())
      setEvents(await api.events())
      setAuditEvents(await api.auditEvents())
    })
  }

  async function grantPro(userID: string) {
    await mutate('Pro access granted.', async () => {
      await api.grantPro(userID)
      await refreshUsersSummaryAudit()
      if (selectedUser?.user.id === userID) await openUser(userID)
    })
  }

  async function revokePro(userID: string) {
    await mutate('Pro access revoked.', async () => {
      await api.revokePro(userID)
      await refreshUsersSummaryAudit()
      if (selectedUser?.user.id === userID) await openUser(userID)
    })
  }

  async function resetTrialUsed(userID: string) {
    await mutate('Trial eligibility reset.', async () => {
      await api.resetTrialUsed(userID)
      await refreshUsersSummaryAudit()
      if (selectedUser?.user.id === userID) await openUser(userID)
    })
  }

  async function openUser(userID: string) {
    setLoading(true)
    try {
      const detail = await api.user(userID)
      setSelectedUser(detail)
      setUserDraft(makeUserDraft(detail.user))
      setStatus({ message: 'User detail loaded.', kind: 'ok' })
    } catch (error) {
      setStatus({ message: errorMessage(error), kind: 'err' })
    } finally {
      setLoading(false)
    }
  }

  async function applyUserFocus(focus: UserFocus) {
    const lookup = focus.userID || focus.installID || focus.ip || ''
    if (lookup) setUserQuery(lookup)
    setUserTier('')
    setLoading(true)
    try {
      let nextUsers = users
      if (lookup) {
        nextUsers = await api.users(lookup, '')
        setUsers(nextUsers)
      }
      const userID = focus.userID || (nextUsers.length === 1 ? nextUsers[0].id : '')
      if (userID) {
        const detail = await api.user(userID)
        setSelectedUser(detail)
        setUserDraft(makeUserDraft(detail.user))
      } else {
        setSelectedUser(null)
        setUserDraft(null)
      }
      setStatus({ message: userFocusStatus(focus, userID), kind: 'ok' })
    } catch (error) {
      setStatus({ message: errorMessage(error), kind: 'err' })
    } finally {
      setLoading(false)
    }
  }

  function openUserFocus(focus: UserFocus) {
    const normalized = normalizeUserFocus(focus)
    if (!normalized) return
    const key = userFocusKey(normalized)
    setUserFocus(normalized)
    appliedUserFocusKey.current = key
    navigatePage('users', userFocusSearch(normalized))
    void applyUserFocus(normalized)
  }

  function clearUserFocus() {
    setUserFocus(null)
    appliedUserFocusKey.current = ''
    navigatePage('users')
  }

  async function saveUser() {
    if (!selectedUser || !userDraft) return
    await mutate('User updated.', async () => {
      const detail = await api.updateUser(selectedUser.user.id, {
        email: userDraft.email.trim(),
        subscription_status: userDraft.status,
        product_id: userDraft.productID.trim(),
        environment: userDraft.status === 'trial' ? 'signup_trial' : userDraft.environment.trim(),
        expires_at: localDateTimeToISOString(userDraft.expiresAt),
      })
      setSelectedUser(detail)
      setUserDraft(makeUserDraft(detail.user))
      await refreshUsersSummaryAudit()
    })
  }

  async function saveSignupTrial(event?: FormEvent) {
    event?.preventDefault()
    await mutate('Signup trial updated.', async () => {
      const next = await api.updateSignupTrial({
        enabled: trialDraft.enabled,
        start_at: localDateTimeToISOString(trialDraft.startAt),
        end_at: localDateTimeToISOString(trialDraft.endAt),
        days: Number(trialDraft.days) || 30,
        product_id: trialDraft.productID.trim() || 'promo.signup_trial',
      })
      setSignupTrial(next)
      setTrialDraft(makeTrialDraft(next))
      setSummary(await api.summary())
      setAuditEvents(await api.auditEvents())
    })
  }

  async function createPromoCode(payload: AdminPromoCodeCreate) {
    await mutate(`Promo code ${payload.code} created.`, async () => {
      await api.createPromoCode(payload)
      setPromoCodes(await api.promoCodes())
      setAuditEvents(await api.auditEvents())
    })
  }

  async function updatePromoCode(code: string, payload: AdminPromoCodeUpdate) {
    await mutate(`Promo code ${code} updated.`, async () => {
      await api.updatePromoCode(code, payload)
      setPromoCodes(await api.promoCodes())
      setAuditEvents(await api.auditEvents())
    })
  }

  async function deletePromoCode(code: string) {
    await mutate(`Promo code ${code} deleted.`, async () => {
      await api.deletePromoCode(code)
      setPromoCodes(await api.promoCodes())
      setAuditEvents(await api.auditEvents())
    })
  }

  async function saveSettingsSection(_sectionID: string, items: SaveItem[]) {
    if (items.length === 0) return
    await mutate('Settings saved.', async () => {
      const next = await api.updateConfig(items)
      setConfig(next)
      setAuditEvents(await api.auditEvents())
    })
  }

  async function crawlNow() {
    await mutate('Collector crawl requested. Watching for runtime events.', async () => {
      await api.crawlNow('admin-console')
      await sleep(1200)
      setEvents(await api.events())
      setSummary(await api.summary())
      setAuditEvents(await api.auditEvents())
    })
  }

  async function refreshUsersSummaryAudit() {
    const [usersNext, subsNext, webSubsNext, summaryNext, uoaNext, auditNext] = await Promise.all([
      api.users(userQuery, userTier),
      api.subscriptions(subscriptionQuery, subscriptionStatus),
      api.webSubscriptions(subscriptionQuery, subscriptionStatus),
      api.summary(),
      api.uoaOverview(),
      api.auditEvents(),
    ])
    setUsers(usersNext)
    setSubscriptions(subsNext)
    setWebSubscriptionsState(webSubsNext)
    setSummary(summaryNext)
    setUoaOverview(uoaNext)
    setAuditEvents(auditNext)
  }

  async function mutate(success: string, fn: () => Promise<void>) {
    setLoading(true)
    try {
      await fn()
      setStatus({ message: success, kind: 'ok' })
    } catch (error) {
      setStatus({ message: errorMessage(error), kind: 'err' })
    } finally {
      setLoading(false)
    }
  }

  if (sessionState === 'checking') {
    return <SessionCheckScreen status={status} />
  }

  if (sessionState === 'signed_out') {
    return (
      <LoginScreen
        apiBase={apiBase}
        setAPIBase={setAPIBase}
        setupKey={setupKey}
        setSetupKey={setSetupKey}
        passkeyStatus={passkeyStatus}
        status={status}
        loading={loading}
        onRegister={registerPasskey}
        onLogin={signInPasskey}
      />
    )
  }

  const currentPage = pageMeta(page)

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">N</div>
          <div>
            <div className="brand-name">NavOption</div>
            <div className="brand-sub">Admin</div>
          </div>
        </div>
        <nav className="nav-list" aria-label="Admin navigation">
          {navGroups.map((group) => (
            <div className="nav-section" key={group}>
              <div className="nav-section-label">{group}</div>
              {pages.filter((item) => item.group === group).map((item) => (
                <button key={item.id} className={page === item.id ? 'active' : ''} type="button" onClick={() => navigatePage(item.id)}>
                  <span>{item.icon}</span>
                  <strong>{item.label}</strong>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <span className={isConnected ? 'dot ok' : 'dot'} />
          <span>{isConnected ? 'Connected' : 'Signed out'}</span>
        </div>
      </aside>

      <main className="workspace">
        <header className="workspace-top">
          <div className="workspace-title">
            <div className="eyebrow">{currentPage.eyebrow}</div>
            <h1>{currentPage.title}</h1>
            <p>{currentPage.description}</p>
          </div>
          <div className="top-actions">
            <button type="button" className="ghost" onClick={refreshCurrentPage} disabled={loading || !sessionReady}>Refresh</button>
            <button type="button" className="ghost" onClick={signOut}>Sign out</button>
          </div>
        </header>

        <RuntimeStrip summary={summary} config={config} loading={loading} sessionReady={sessionReady} />
        <section className={'status ' + status.kind}>{status.message}</section>

        {page === 'overview' && (
          <Overview
            summary={summary}
            config={config}
            health={summary?.collector_health ?? null}
            trial={signupTrial}
            events={events}
            auditEvents={auditEvents}
            onNavigate={navigatePage}
          />
        )}
        {page === 'users' && (
          <Users
            users={users}
            query={userQuery}
            tier={userTier}
            setQuery={setUserQuery}
            setTier={setUserTier}
            onSearch={searchUsers}
            selected={selectedUser}
            focus={userFocus}
            draft={userDraft}
            setDraft={setUserDraft}
            onOpen={(userID) => openUserFocus({ userID })}
            onClose={() => {
              setSelectedUser(null)
              setUserDraft(null)
            }}
            onClearFocus={clearUserFocus}
            onSave={saveUser}
            onGrant={grantPro}
            onRevoke={revokePro}
            onResetTrial={resetTrialUsed}
          />
        )}
        {page === 'subscriptions' && (
          <>
            <Subscriptions
              subscriptions={subscriptions}
              query={subscriptionQuery}
              status={subscriptionStatus}
              setQuery={setSubscriptionQuery}
              setStatus={setSubscriptionStatus}
              onSearch={searchSubscriptions}
              onOpenUser={openUserFocus}
            />
            <WebSubscriptionsLedger subscriptions={webSubscriptions} onOpenUser={openUserFocus} />
          </>
        )}
        {page === 'options' && (
          <OptionsUniverse
            items={optionUniverse}
            categories={optionCategories}
            crawlerSettings={crawlerSettings}
            query={optionQuery}
            bulk={optionBulk}
            setQuery={setOptionQuery}
            setBulk={setOptionBulk}
            onSearch={searchOptionUniverse}
            onSave={saveOptionSymbols}
            onToggle={setOptionSymbolEnabled}
            onDelete={deleteOptionSymbol}
            onCrawlNow={crawlNow}
            onMoveSymbols={moveSymbolsToCategory}
            onCreateCategory={createOptionCategory}
            onRenameCategory={renameOptionCategory}
            onDeleteCategory={removeOptionCategory}
            onSaveCrawlerSettings={saveCrawlerSettings}
          />
        )}
        {page === 'uoa' && (
          <UOAOps overview={uoaOverview} config={config} onDetectNow={triggerUOADetect} />
        )}
        {page === 'growth' && (
          <>
            <Promotions trial={signupTrial} draft={trialDraft} setDraft={setTrialDraft} onSave={saveSignupTrial} />
            <PromoCodes
              codes={promoCodes}
              onCreate={createPromoCode}
              onUpdate={updatePromoCode}
              onDelete={deletePromoCode}
            />
          </>
        )}
        {page === 'settings' && (
          <SettingsPanel config={config} onSaveSection={saveSettingsSection} />
        )}
        {page === 'operations' && (
          <Operations summary={summary} config={config} health={summary?.collector_health ?? null} events={events} onCrawlNow={crawlNow} />
        )}
        {page === 'audit' && <Audit events={auditEvents} />}
        {page === 'metrics' && <ProductMetrics weeks={metricsWeeks} />}
      </main>
    </div>
  )
}

function SessionCheckScreen({ status }: { status: Status }) {
  return (
    <main className="login-page">
      <section className="login-card">
        <div className="login-brand">
          <div className="brand-mark">N</div>
          <div>
            <div className="brand-name">NavOption</div>
            <div className="brand-sub">Admin</div>
          </div>
        </div>
        <section className={'status ' + status.kind}>{status.message}</section>
      </section>
    </main>
  )
}

function LoginScreen(props: {
  apiBase: string
  setAPIBase: (value: string) => void
  setupKey: string
  setSetupKey: (value: string) => void
  passkeyStatus: PasskeyStatus | null
  status: Status
  loading: boolean
  onRegister: (event?: FormEvent) => void
  onLogin: (event?: FormEvent) => void
}) {
  const registered = props.passkeyStatus?.registered
  const passkeyKnown = props.passkeyStatus !== null
  return (
    <main className="login-page">
      <section className="login-card">
        <div className="login-brand">
          <div className="brand-mark">N</div>
          <div>
            <div className="brand-name">NavOption</div>
            <div className="brand-sub">Admin</div>
          </div>
        </div>
        <form className="login-form" onSubmit={passkeyKnown ? (registered ? props.onLogin : props.onRegister) : (e) => e.preventDefault()}>
          <label>
            <span>API</span>
            <input value={props.apiBase} onChange={(e) => props.setAPIBase(e.target.value)} type="url" spellCheck={false} />
          </label>
          {passkeyKnown && !registered && (
            <label>
              <span>Setup key</span>
              <input value={props.setupKey} onChange={(e) => props.setSetupKey(e.target.value)} type="password" autoComplete="current-password" spellCheck={false} />
            </label>
          )}
          <button type="submit" disabled={props.loading || !passkeyKnown || (!registered && !props.setupKey.trim())}>
            {passkeyKnown ? (registered ? 'Sign in with Passkey' : 'Create Passkey') : 'Checking passkey...'}
          </button>
        </form>
        <section className={'status ' + props.status.kind}>{props.status.message}</section>
      </section>
    </main>
  )
}

function Overview({ summary, config, health, trial, events, auditEvents, onNavigate }: {
  summary: Summary | null
  config: AdminConfig | null
  health: CollectorHealth | null
  trial: SignupTrial | null
  events: CollectorEvent[]
  auditEvents: AdminAuditEvent[]
  onNavigate: (page: Page) => void
}) {
  const actionItems = buildActionItems(summary, config, health, trial, events)
  const proRate = summary?.users ? Math.round(((summary.active_subscriptions ?? 0) / summary.users) * 100) : 0
  return (
    <div className="stack">
      <section className="stats-grid">
        <Metric label="Users" value={fmtNum(summary?.users)} delta={fmtNum(summary?.new_users_7d) + ' new 7d'} />
        <Metric label="Pro" value={fmtNum(summary?.active_subscriptions)} delta={fmtNum(summary?.active_trials) + ' trials'} tone="good" />
        <Metric label="Watchlist" value={fmtNum(summary?.watchlist_items)} delta={fmtNum(summary?.presets) + ' presets'} />
        <Metric label="Push tokens" value={fmtNum(summary?.device_tokens)} delta={fmtNum(summary?.notification_messages_24h) + ' messages 24h'} tone={summary?.device_tokens ? 'good' : 'warn'} />
        <Metric label="Analyze" value={fmtNum(summary?.analyze_runs_24h)} delta="runs last 24h" />
        <Metric label="Positions" value={fmtNum(summary?.active_positions)} delta={fmtNum(summary?.pending_price_alerts) + ' pending alerts'} />
        <Metric label="StoreKit" value={fmtNum(summary?.storekit_transactions)} delta="tracked txns" />
        <Metric label="Events" value={fmtNum(summary?.events_24h)} delta="collector 24h" tone="warn" />
      </section>

      <section className="overview-main">
        <Panel title="Action Queue" kicker="Command Center">
          <ActionQueue items={actionItems} onNavigate={onNavigate} />
        </Panel>
        <Panel title="Business Snapshot" kicker="Live">
          <dl className="kv-list">
            <div><dt>New users</dt><dd>{fmtNum(summary?.new_users_7d)} / 7d · {fmtNum(summary?.new_users_30d)} / 30d</dd></div>
            <div><dt>Pro conversion</dt><dd>{fmtNum(proRate)}%</dd></div>
            <div><dt>Active trials</dt><dd>{fmtNum(summary?.active_trials)}</dd></div>
            <div><dt>Push coverage</dt><dd>{fmtNum(summary?.device_tokens)} device tokens</dd></div>
            <div><dt>Signup trial</dt><dd className={trial?.enabled ? 'ok-text' : 'warn-text'}>{trial?.enabled ? 'enabled' : 'disabled'}</dd></div>
          </dl>
        </Panel>
      </section>

      <section className="overview-grid">
        <Panel title="System Health" kicker="Runtime">
          <HealthList health={health} />
        </Panel>
        <Panel title="Growth Campaign" kicker="Promotion">
          <TrialList trial={trial} />
        </Panel>
        <Panel title="LLM Provider" kicker="Configuration">
          <dl className="kv-list">
            <div><dt>Provider</dt><dd>{config?.llm.provider || '-'}</dd></div>
            <div><dt>LLM model</dt><dd>{config?.llm.model || '-'}</dd></div>
            <div><dt>LLM key</dt><dd className={config?.llm.api_key_configured ? 'ok-text' : 'bad-text'}>{config?.llm.api_key_configured ? 'configured' : 'missing'}</dd></div>
          </dl>
        </Panel>
        <Panel title="Recent Admin Activity" kicker="Audit">
          <ActivityList events={auditEvents.slice(0, 5)} />
        </Panel>
        <Panel title="Collector Activity" kicker="Operations">
          <EventList events={events.slice(0, 5)} emptyText="Collector has not recorded events yet." />
        </Panel>
      </section>
    </div>
  )
}

function ActionQueue({ items, onNavigate }: { items: ActionItem[]; onNavigate: (page: Page) => void }) {
  if (items.length === 0) {
    return (
      <div className="clear-state">
        <strong>All clear</strong>
        <span>No immediate admin action detected.</span>
      </div>
    )
  }
  return (
    <ol className="action-list">
      {items.map((item) => (
        <li key={item.title}>
          <div>
            <span className={'severity ' + item.priority}>{item.priority}</span>
            <strong>{item.title}</strong>
            <p>{item.body}</p>
          </div>
          <button type="button" className="small ghost" onClick={() => onNavigate(item.page)}>Open</button>
        </li>
      ))}
    </ol>
  )
}

function Users({ users, query, tier, setQuery, setTier, selected, focus, draft, setDraft, onSearch, onOpen, onClose, onClearFocus, onSave, onGrant, onRevoke, onResetTrial }: {
  users: AdminUser[]
  query: string
  tier: string
  setQuery: (value: string) => void
  setTier: (value: string) => void
  selected: AdminUserDetail | null
  focus: UserFocus | null
  draft: UserDraft | null
  setDraft: React.Dispatch<React.SetStateAction<UserDraft | null>>
  onSearch: (event: FormEvent) => void
  onOpen: (userID: string) => void
  onClose: () => void
  onClearFocus: () => void
  onSave: () => void
  onGrant: (userID: string) => void
  onRevoke: (userID: string) => void
  onResetTrial: (userID: string) => void
}) {
  return (
    <section className={selected ? 'users-layout has-selection' : 'users-layout'}>
      <Panel title="Accounts & Sessions" kicker="Identity Records" actions={(
        <form className="toolbar" onSubmit={onSearch}>
          <input value={query} onChange={(e) => setQuery(e.target.value)} type="search" placeholder="email, apple sub, user id, install, IP" spellCheck={false} />
          <select value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="">All tiers</option>
            <option value="pro">Pro</option>
            <option value="trial">Trial</option>
            <option value="free">Free</option>
          </select>
          <button type="submit">Search</button>
        </form>
      )}>
        {focus && (
          <div className="context-banner">
            <div>
              <strong>Opened from subscription context</strong>
              <span>{userFocusLabel(focus)}</span>
            </div>
            <button type="button" className="small ghost" onClick={onClearFocus}>Clear context</button>
          </div>
        )}
        <div className="callout">
          Search accepts email, Apple sub, user id, install id, or device IP. Use the detail panel to verify entitlement, StoreKit linkage, device footprint, and recent behavior before granting or revoking access.
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Subscription</th>
                <th>Usage</th>
                <th>Seen</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={5} className="empty-row">No users found.</td></tr>
              ) : users.map((user) => {
                const entitlement = describeEntitlement(user)
                const actions = userActionState(user)
                return (
                  <tr key={user.id} className={selected?.user.id === user.id ? 'selected-row' : ''}>
                    <td data-label="User">
                      <strong>{user.email || '(no email)'}</strong>
                      <span className={'pill ' + (user.auth_provider === 'guest' ? 'warn' : '')}>{user.auth_provider || 'apple'}</span>
                      {user.merged_into_user_id && <span className="pill warn">merged</span>}
                      <div className="mono">{user.id}</div>
                      <div className="mono">{user.apple_sub || '-'}</div>
                      {user.guest_install_id && <div className="mono">install {user.guest_install_id}</div>}
                    </td>
                    <td data-label="Subscription">
                      <span className={'pill ' + (entitlement.isActive ? 'pro' : entitlement.state === 'expired' ? 'warn' : '')}>{entitlement.label}</span>
                      {entitlement.isTrial && entitlement.label !== 'TRIAL' && <span className="pill warn">TRIAL</span>}
                      <div className="mono">{entitlement.productLabel}</div>
                      <div className="mono muted-line">{entitlement.productDetail}</div>
                      <div className="mono">{fmtDate(user.expires_at)}</div>
                    </td>
                    <td data-label="Usage">
                      <div>{fmtNum(user.watchlist_count)} watchlist</div>
                      <div className="mono">{fmtNum(user.preset_count)} presets</div>
                      <div className="mono">{fmtNum(user.device_token_count)} devices · {fmtNum(user.notification_message_count)} notifications</div>
                      <div className="mono">{fmtNum(user.open_position_count)} open positions · {fmtNum(user.analyze_run_count)} analyze</div>
                    </td>
                    <td data-label="Seen">
                      <div>{fmtDate(user.last_seen_at)}</div>
                      <div className="mono">created {fmtDate(user.created_at)}</div>
                    </td>
                    <td className="right" data-label="Actions">
                      <div className="actions">
                        <button type="button" className="small" onClick={() => onOpen(user.id)}>Details</button>
                        {actions.canGrant && <button type="button" className="small" onClick={() => onGrant(user.id)}>{actions.grantLabel}</button>}
                        {actions.canRevoke && <button type="button" className="small danger" onClick={() => onRevoke(user.id)}>{actions.revokeLabel}</button>}
                        {actions.note && <span className="action-note">{actions.note}</span>}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Panel>
      {selected && draft && (
        <UserDetailPanel
          detail={selected}
          focus={focus}
          draft={draft}
          setDraft={setDraft}
          onClose={onClose}
          onSave={onSave}
          onGrant={() => onGrant(selected.user.id)}
          onRevoke={() => onRevoke(selected.user.id)}
          onResetTrial={() => onResetTrial(selected.user.id)}
        />
      )}
    </section>
  )
}

function UserDetailPanel({ detail, focus, draft, setDraft, onClose, onSave, onGrant, onRevoke, onResetTrial }: {
  detail: AdminUserDetail
  focus: UserFocus | null
  draft: UserDraft
  setDraft: React.Dispatch<React.SetStateAction<UserDraft | null>>
  onClose: () => void
  onSave: () => void
  onGrant: () => void
  onRevoke: () => void
  onResetTrial: () => void
}) {
  const entitlement = describeEntitlement(detail.user)
  const actions = userActionState(detail.user)
  const footprints = buildUserDeviceFootprints(detail)
  const subscriptionLinks = buildUserSubscriptionLinks(detail)
  const behaviorEvents = buildUserBehaviorEvents(detail)
  return (
    <Panel title="User Detail" kicker="Operator View" actions={<button type="button" className="ghost" onClick={onClose}>Close</button>}>
      <div className="detail-stack">
        {focus && <div className="focus-card"><strong>Subscription context</strong><span>{userFocusLabel(focus)}</span></div>}
        <section className="user-command-grid">
          <article className="info-card">
            <div className="info-card-head">
              <span className="eyebrow">Identity</span>
              <span className={'pill ' + (detail.user.auth_provider === 'guest' ? 'warn' : '')}>{detail.user.auth_provider || 'apple'}</span>
            </div>
            <strong>{detail.user.email || '(no email)'}</strong>
            <dl className="kv-list compact">
              <div><dt>User ID</dt><dd>{detail.user.id}</dd></div>
              <div><dt>Apple sub</dt><dd>{detail.user.apple_sub || '-'}</dd></div>
              <div><dt>Guest install</dt><dd>{detail.user.guest_install_id || '-'}</dd></div>
              <div><dt>Merged into</dt><dd>{detail.user.merged_into_user_id || '-'}</dd></div>
              <div><dt>Created</dt><dd>{fmtDate(detail.user.created_at)}</dd></div>
              <div><dt>Last seen</dt><dd>{fmtDate(detail.user.last_seen_at)}</dd></div>
            </dl>
          </article>
          <article className="info-card">
            <div className="info-card-head">
              <span className="eyebrow">Entitlement</span>
              <span className={'pill ' + (entitlement.isActive ? 'pro' : entitlement.state === 'expired' ? 'warn' : '')}>{entitlement.label}</span>
            </div>
            <strong>{entitlement.productLabel}</strong>
            <dl className="kv-list compact">
              <div><dt>Product</dt><dd>{entitlement.productDetail || '-'}</dd></div>
              <div><dt>Environment</dt><dd>{detail.user.environment || '-'}</dd></div>
              <div><dt>Expires</dt><dd>{fmtDate(detail.user.expires_at)}</dd></div>
              <div><dt>StoreKit txns</dt><dd>{fmtNum(detail.storekit_transactions?.length)}</dd></div>
              <div><dt>Restore events</dt><dd>{fmtNum(detail.storekit_restore_events?.length)}</dd></div>
              <div><dt>Push devices</dt><dd>{fmtNum(detail.device_tokens?.length)}</dd></div>
            </dl>
          </article>
        </section>
        <section className="diagnostic-grid">
          <MiniStat label="Watchlist" value={fmtNum(detail.watchlist?.length)} />
          <MiniStat label="Positions" value={fmtNum(detail.positions?.length)} />
          <MiniStat label="Alerts" value={fmtNum(detail.price_alerts?.length)} />
          <MiniStat label="Analyze" value={fmtNum(detail.analyze_runs?.length)} />
          <MiniStat label="Messages" value={fmtNum(detail.notification_messages?.length)} />
        </section>
        <section className="detail-section">
          <h3>Install / Device / IP Footprint</h3>
          {footprints.length === 0 ? (
            <div className="empty-row">No install, device token, or IP footprint recorded.</div>
          ) : (
            <div className="table-wrap footprint-table user-footprint-table">
              <table>
                <thead>
                  <tr>
                    <th>Install</th>
                    <th>Device IP</th>
                    <th>Runtime</th>
                    <th>Subscription Links</th>
                  </tr>
                </thead>
                <tbody>
                  {footprints.map((item) => (
                    <tr key={item.key} className={matchesUserFocus(item, focus) ? 'selected-row' : ''}>
                      <td data-label="Install"><strong>{item.installID || 'No install id'}</strong></td>
                      <td data-label="Device IP">
                        {item.ips.length ? item.ips.map((ip) => <span className="pill" key={ip}>{ip}</span>) : <span className="muted-line">No IP recorded</span>}
                      </td>
                      <td data-label="Runtime">
                        <div>{item.platforms.join(', ') || '-'}</div>
                        <div className="mono">{item.environments.join(', ') || '-'}</div>
                        <div className="mono">last {fmtDate(item.lastSeenAt)}</div>
                      </td>
                      <td data-label="Subscription Links">
                        <div>{fmtNum(item.restoreCount)} restore/sync · {fmtNum(item.tokenCount)} tokens</div>
                        <div className="mono">{item.txnIDs.slice(0, 3).join(', ') || '-'}</div>
                        <div className="mono">{item.sources.join(', ') || '-'}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className="detail-section">
          <h3>StoreKit Linkage</h3>
          {subscriptionLinks.length === 0 ? (
            <div className="empty-row">No StoreKit purchase or restore linkage.</div>
          ) : (
            <div className="table-wrap user-subscription-table">
              <table>
                <thead>
                  <tr>
                    <th>Original Transaction</th>
                    <th>Product</th>
                    <th>Install / IP</th>
                    <th>Lifecycle</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionLinks.map((item) => (
                    <tr key={item.key} className={focus?.txnID && item.txnID === focus.txnID ? 'selected-row' : ''}>
                      <td data-label="Original Transaction">
                        <strong>{item.txnID || '-'}</strong>
                        <div className="mono">{item.source}</div>
                      </td>
                      <td data-label="Product">
                        <span className={'pill ' + (item.status === 'active' ? 'pro' : item.status === 'expired' ? 'warn' : '')}>{item.status.toUpperCase()}</span>
                        <div>{item.product}</div>
                      </td>
                      <td data-label="Install / IP">
                        <div className="mono">{item.installID ? 'install ' + item.installID : 'no install id'}</div>
                        <div className="mono">IP {item.ip || '-'}</div>
                      </td>
                      <td data-label="Lifecycle">
                        <div>expires {fmtDate(item.expiresAt)}</div>
                        <div className="mono">seen {fmtDate(item.lastSeenAt)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <DetailSection title="Recent Behavior" empty="No user behavior recorded yet.">
          {behaviorEvents.slice(0, 8).map((item) => (
            <li key={item.key}>
              <strong>{item.title}</strong>
              <span>{item.body}</span>
              <span>{fmtDate(item.at)}</span>
            </li>
          ))}
        </DetailSection>
        <section className="admin-edit">
          <div>
            <h3>Admin Entitlement Edit</h3>
            <p>Use this only after the identity, StoreKit linkage, and device footprint above match the support case.</p>
          </div>
          <div className="form-grid">
          <label className="field wide">
            <span>Email</span>
            <input value={draft.email} onChange={(e) => setDraft((prev) => prev ? { ...prev, email: e.target.value } : prev)} spellCheck={false} />
          </label>
          <label className="field">
            <span>Status</span>
            <select value={draft.status} onChange={(e) => setDraft((prev) => {
              if (!prev) return prev
              const status = e.target.value
              return {
                ...prev,
                status,
                productID: defaultProductIDForStatus(status, prev.productID),
                environment: status === 'trial' ? 'signup_trial' : prev.environment,
              }
            })}>
              <option value="">No change</option>
              <option value="active">Active Pro</option>
              <option value="trial">Trial</option>
              <option value="free">Free</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label className="field">
            <span>Expires</span>
            <input type="datetime-local" value={draft.expiresAt} onChange={(e) => setDraft((prev) => prev ? { ...prev, expiresAt: e.target.value } : prev)} />
          </label>
          <label className="field">
            <span>Product ID</span>
            <input value={draft.productID} onChange={(e) => setDraft((prev) => prev ? { ...prev, productID: e.target.value } : prev)} spellCheck={false} />
          </label>
          <label className="field">
            <span>Environment</span>
            <input value={draft.environment} onChange={(e) => setDraft((prev) => prev ? { ...prev, environment: e.target.value } : prev)} spellCheck={false} />
          </label>
          <div className="actions wide">
            <button type="button" onClick={onSave}>Save User</button>
            {actions.canGrant && <button type="button" className="small" onClick={onGrant}>{actions.grantLabel}</button>}
            {actions.canRevoke && <button type="button" className="small danger" onClick={onRevoke}>{actions.revokeLabel}</button>}
            {actions.note && <span className="action-note">{actions.note}</span>}
          </div>
          <div className="field wide" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, fontSize: '0.85rem' }}>
            <span style={{ color: 'var(--muted, #888)' }}>Trial:</span>
            <span>
              {detail.user.trial_used_at
                ? `Used on ${new Date(detail.user.trial_used_at).toLocaleString()}`
                : 'Not used (eligible for Stripe trial)'}
            </span>
            {detail.user.trial_used_at && (
              <button
                type="button"
                className="small"
                onClick={() => {
                  if (window.confirm('Reset trial eligibility for this user? They will be offered the Stripe trial period again on their next checkout.')) {
                    onResetTrial()
                  }
                }}
              >
                Reset trial
              </button>
            )}
          </div>
          </div>
        </section>
        <section className="detail-grid secondary-detail-grid">
          <DetailSection title="Notification Preferences" empty="No explicit preference overrides.">
            {(detail.notification_preferences ?? []).map((item) => (
              <li key={item.kind}>
                <strong>{labelAction(item.kind)}</strong>
                <span className={item.enabled ? 'ok-text' : 'warn-text'}>{item.enabled ? 'enabled' : 'disabled'} · {fmtDate(item.updated_at)}</span>
              </li>
            ))}
          </DetailSection>
          <DetailSection title="Positions" empty="No tracked positions.">
            {(detail.positions ?? []).map((item) => (
              <li key={item.id}>
                <strong>{item.symbol} {item.side} {item.strike} · {item.status}</strong>
                <span>{item.qty}x · premium {item.premium} · expiry {item.expiry}</span>
                <span>opened {fmtDate(item.opened_at)}{item.closed_at ? ' · closed ' + fmtDate(item.closed_at) : ''}</span>
                {item.notes && <span>{item.notes}</span>}
              </li>
            ))}
          </DetailSection>
          <DetailSection title="Price Alerts" empty="No price alerts.">
            {(detail.price_alerts ?? []).map((item) => (
              <li key={item.id}>
                <strong>{item.symbol} {item.side} {item.target_price}</strong>
                <span>{item.status} · created {fmtDate(item.created_at)}</span>
                {item.last_seen_price && <span>last seen {item.last_seen_price} · checked {fmtDate(item.last_checked_at)}</span>}
              </li>
            ))}
          </DetailSection>
          <DetailSection title="Watchlist" empty="No watchlist items.">
            {(detail.watchlist ?? []).map((item) => (
              <li key={item.id}><strong>{item.symbol}</strong><span>#{item.position} · {fmtDate(item.created_at)}</span></li>
            ))}
          </DetailSection>
          <DetailSection title="Presets" empty="No presets.">
            {(detail.presets ?? []).map((item) => (
              <li key={item.id}>
                <strong>{item.name}{item.is_default ? ' · default' : ''}</strong>
                <StructuredValue value={item.filters} />
              </li>
            ))}
          </DetailSection>
          <DetailSection title="Analyze Runs" empty="No analyze runs.">
            {(detail.analyze_runs ?? []).map((item) => (
              <li key={item.id}>
                <strong>{item.symbol} · {item.mode} · {item.status}</strong>
                <span>{item.final_action || 'no final action'}{item.weighted_score ? ' · score ' + item.weighted_score : ''}</span>
                <span>{item.session_id}</span>
                <span>{fmtDate(item.started_at)}{item.finished_at ? ' → ' + fmtDate(item.finished_at) : ''}</span>
              </li>
            ))}
          </DetailSection>
          <DetailSection title="Notification Messages" empty="No notification history.">
            {(detail.notification_messages ?? []).slice(0, 12).map((item) => (
              <li key={item.id}>
                <strong>{labelAction(item.kind)}{item.symbol ? ' · ' + item.symbol : ''}</strong>
                <span>{item.title || item.message}</span>
                {item.deep_link && <span>{item.deep_link}</span>}
                <span>{fmtDate(item.created_at)}</span>
              </li>
            ))}
          </DetailSection>
        </section>
      </div>
    </Panel>
  )
}

function Subscriptions({ subscriptions, query, status, setQuery, setStatus, onSearch, onOpenUser }: {
  subscriptions: AdminSubscriptionLedgerItem[]
  query: string
  status: string
  setQuery: (value: string) => void
  setStatus: (value: string) => void
  onSearch: (event: FormEvent) => void
  onOpenUser: (focus: UserFocus) => void
}) {
  const [expandedTxn, setExpandedTxn] = useState<string>('')
  const activeCount = subscriptions.filter((item) => storeKitLedgerState(item) === 'active').length
  const linkedUserCount = new Set(subscriptions.flatMap((item) => [
    item.current_user_id,
    ...(item.restore_events ?? []).map((event) => event.user_id),
  ].filter(Boolean) as string[])).size
  const linkedInstallCount = new Set(subscriptions.flatMap((item) => [
    item.current_app_install_id,
    ...(item.restore_events ?? []).map((event) => event.app_install_id),
  ].filter(Boolean) as string[])).size
  const eventCount = subscriptions.reduce((sum, item) => sum + item.restore_event_count, 0)
  return (
    <section className="stack">
      <section className="stats-grid">
        <Metric label="Active" value={fmtNum(activeCount)} delta="currently valid StoreKit subscriptions" tone={activeCount ? 'good' : 'warn'} />
        <Metric label="Linked users" value={fmtNum(linkedUserCount)} delta="seen in current link or restore history" />
        <Metric label="Installs" value={fmtNum(linkedInstallCount)} delta="distinct app install ids" />
        <Metric label="Events" value={fmtNum(eventCount)} delta="purchase / restore / sync records" />
      </section>
      <Panel title="StoreKit Ledger" kicker="Subscriptions" actions={(
        <form className="toolbar" onSubmit={onSearch}>
          <input value={query} onChange={(e) => setQuery(e.target.value)} type="search" placeholder="txn, user, email, install, product" spellCheck={false} />
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
          <button type="submit">Search</button>
        </form>
      )}>
        <div className="callout">
          StoreKit subscriptions are keyed by Apple original transaction ID. Apple does not expose the buyer's iTunes email; counts below show how many NavOption users and install IDs have restored or synced the same purchase.
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Subscription</th>
                <th>Latest Link</th>
                <th>Restore Footprint</th>
                <th>Lifecycle</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr><td colSpan={5} className="empty-row">No StoreKit subscriptions found.</td></tr>
              ) : subscriptions.map((item) => {
                const ledgerState = storeKitLedgerState(item)
                const active = ledgerState === 'active'
                const expanded = expandedTxn === item.original_txn_id
                return (
                  <React.Fragment key={item.original_txn_id}>
                    <tr>
                      <td data-label="Subscription">
                        <strong>{productDisplayName(item.product_id, item.environment)}</strong>
                        <span className={'pill ' + (active ? 'pro' : ledgerState === 'expired' ? 'warn' : '')}>{ledgerState.toUpperCase()}</span>
                        <div className="mono">{item.original_txn_id}</div>
                        <div className="mono muted-line">{item.product_id}</div>
                      </td>
                      <td data-label="Latest Link">
                        <div>{item.current_user_email || item.current_user_id || '-'}</div>
                        <div className="mono">{item.current_auth_provider || '-'}</div>
                        {item.current_app_install_id && <div className="mono">install {item.current_app_install_id}</div>}
                        {item.current_user_id && (
                          <button
                            type="button"
                            className="small ghost inline-action"
                            onClick={() => onOpenUser({
                              userID: item.current_user_id,
                              installID: item.current_app_install_id,
                              txnID: item.original_txn_id,
                              source: 'latest-link',
                            })}
                          >
                            Open user
                          </button>
                        )}
                      </td>
                      <td data-label="Restore Footprint">
                        <div>{fmtNum(item.restored_user_count)} users · {fmtNum(item.restored_install_count)} installs</div>
                        <div className="mono">{fmtNum(item.restore_event_count)} purchase / restore / sync events</div>
                        <div className="mono">last {fmtDate(item.last_restore_at)}</div>
                      </td>
                      <td data-label="Lifecycle">
                        <div>expires {fmtDate(item.expires_at)}</div>
                        <div className="mono">first {fmtDate(item.first_seen_at)}</div>
                        <div className="mono">seen {fmtDate(item.last_seen_at)}</div>
                      </td>
                      <td className="right" data-label="Actions">
                        <button type="button" className="small ghost" onClick={() => setExpandedTxn(expanded ? '' : item.original_txn_id)}>
                          {expanded ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="expanded-row">
                        <td colSpan={5}>
                          <SubscriptionRestoreEvents item={item} onOpenUser={onOpenUser} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </section>
  )
}

function SubscriptionRestoreEvents({ item, onOpenUser }: { item: AdminSubscriptionLedgerItem; onOpenUser: (focus: UserFocus) => void }) {
  const footprints = buildSubscriptionFootprints(item)
  return (
    <section className="ledger-detail">
      <div className="ledger-summary">
        <MiniStat label="Users" value={fmtNum(item.restored_user_count)} />
        <MiniStat label="Installs" value={fmtNum(item.restored_install_count)} />
        <MiniStat label="Events" value={fmtNum(item.restore_event_count)} />
      </div>
      {footprints.length === 0 ? (
        <div className="empty-row">No install footprint recorded for this subscription.</div>
      ) : (
        <div className="table-wrap footprint-table">
          <table>
            <thead>
              <tr>
                <th>Install</th>
                <th>User</th>
                <th>Device IP</th>
                <th>Activity</th>
              </tr>
            </thead>
            <tbody>
              {footprints.map((footprint) => (
                <tr key={footprint.key}>
                  <td data-label="Install">
                    <strong>{footprint.installID || 'No install id'}</strong>
                    {footprint.installID && (
                      <button
                        type="button"
                        className="small ghost inline-action"
                        onClick={() => onOpenUser({
                          installID: footprint.installID,
                          ip: footprint.ips[0],
                          txnID: item.original_txn_id,
                          source: 'install-footprint',
                        })}
                      >
                        Open install
                      </button>
                    )}
                  </td>
                  <td data-label="User">
                    {footprint.users.length === 0 ? (
                      <span className="muted-line">No linked user</span>
                    ) : footprint.users.map((user) => (
                      <div className="footprint-user" key={user.id || user.label}>
                        <span>{user.label}</span>
                        {user.authProvider && <span className="pill">{user.authProvider}</span>}
                        {user.id && (
                          <button
                            type="button"
                            className="small ghost inline-action"
                            onClick={() => onOpenUser({
                              userID: user.id,
                              installID: footprint.installID,
                              ip: footprint.ips[0],
                              txnID: item.original_txn_id,
                              source: 'restore-footprint',
                            })}
                          >
                            Open user
                          </button>
                        )}
                      </div>
                    ))}
                  </td>
                  <td data-label="Device IP">
                    {footprint.ips.length ? footprint.ips.map((ip) => (
                      <span className="pill" key={ip}>{ip}</span>
                    )) : <span className="muted-line">No IP recorded</span>}
                  </td>
                  <td data-label="Activity">
                    <div>{fmtNum(footprint.eventCount)} events · last {fmtDate(footprint.lastSeenAt)}</div>
                    <div className="mono">{footprint.sources.join(', ') || '-'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

type OptionView = { kind: 'system'; id: 'all' | 'enabled' | 'disabled' | 'uncategorized' }
                 | { kind: 'category'; id: number }

function viewKey(v: OptionView): string {
  return v.kind === 'system' ? `sys:${v.id}` : `cat:${v.id}`
}

function OptionsUniverse({
  items, categories, crawlerSettings, query, bulk, setQuery, setBulk,
  onSearch, onSave, onToggle, onDelete, onCrawlNow,
  onMoveSymbols, onCreateCategory, onRenameCategory, onDeleteCategory, onSaveCrawlerSettings,
}: {
  items: AdminOptionUniverseItem[]
  categories: AdminOptionUniverseCategory[]
  crawlerSettings: AdminCrawlerSettings | null
  query: string
  bulk: string
  setQuery: (value: string) => void
  setBulk: (value: string) => void
  onSearch: (event: FormEvent) => void
  onSave: (categoryID: number | null | undefined, event?: FormEvent) => void | Promise<void>
  onToggle: (item: AdminOptionUniverseItem, enabled: boolean) => void
  onDelete: (symbol: string) => void
  onCrawlNow: () => void
  onMoveSymbols: (symbols: string[], categoryID: number | null) => void | Promise<void>
  onCreateCategory: (name: string, description: string) => void | Promise<void>
  onRenameCategory: (id: number, name: string) => void | Promise<void>
  onDeleteCategory: (id: number, reassignTo: number | null) => void | Promise<void>
  onSaveCrawlerSettings: (payload: Partial<Pick<AdminCrawlerSettings,
    'rate_per_min' | 'burst' | 'workers' | 'min_pass_gap_sec' | 'pass_timeout_sec'>>) => void | Promise<void>
}) {
  const [view, setView] = useState<OptionView>({ kind: 'system', id: 'all' })
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkCategory, setBulkCategory] = useState<string>('')
  const [addCategory, setAddCategory] = useState<string>('')

  const enabledCount = items.filter((item) => item.enabled).length
  const pausedCount = items.length - enabledCount
  const needsDataCount = items.filter((item) => item.enabled && !item.last_aggregate_at).length
  const coveredCount = items.filter((item) => Boolean(item.last_aggregate_at)).length
  const uoaSymbolCount = items.filter((item) => item.uoa_alerts_7d > 0).length
  const uoaAlertCount = items.reduce((sum, item) => sum + item.uoa_alerts_7d, 0)
  const uncategorizedCount = items.filter((item) => !item.category_id).length

  const sortedCategories = [...categories].sort((a, b) => a.position - b.position || a.id - b.id)

  const visibleItems = items.filter((item) => {
    if (view.kind === 'system') {
      if (view.id === 'enabled') return item.enabled
      if (view.id === 'disabled') return !item.enabled
      if (view.id === 'uncategorized') return !item.category_id
      return true
    }
    return item.category_id === view.id
  })

  // Keep selection coherent: drop symbols that have scrolled out of view.
  const visibleSymbols = new Set(visibleItems.map((it) => it.symbol))
  const effectiveSelection = Array.from(selected).filter((s) => visibleSymbols.has(s))
  const allVisibleSelected = visibleItems.length > 0 && effectiveSelection.length === visibleItems.length

  function toggleSelection(symbol: string) {
    const next = new Set(selected)
    if (next.has(symbol)) next.delete(symbol)
    else next.add(symbol)
    setSelected(next)
  }
  function toggleSelectAll() {
    if (allVisibleSelected) setSelected(new Set())
    else setSelected(new Set(visibleItems.map((it) => it.symbol)))
  }

  function handleCreateCategory() {
    const name = window.prompt('Category name? (e.g. "Mega caps", "High IV", "ETFs")', '')
    if (!name || !name.trim()) return
    const description = window.prompt('Optional description (purpose, criteria)', '') ?? ''
    void onCreateCategory(name, description)
  }

  function handleRenameCategory(c: AdminOptionUniverseCategory) {
    const name = window.prompt(`Rename "${c.name}" to`, c.name)
    if (!name || !name.trim() || name.trim() === c.name) return
    void onRenameCategory(c.id, name)
  }

  function handleDeleteCategory(c: AdminOptionUniverseCategory) {
    if (c.is_system) {
      window.alert('System categories cannot be deleted.')
      return
    }
    let reassignTo: number | null = null
    if (c.symbol_count > 0) {
      const targets = sortedCategories.filter((x) => x.id !== c.id)
      const labels = targets.map((x, i) => `  ${i + 1}. ${x.name} (${x.symbol_count})`).join('\n') + `\n  0. Uncategorized`
      const raw = window.prompt(
        `Category "${c.name}" has ${c.symbol_count} symbol${c.symbol_count === 1 ? '' : 's'}.\nReassign them to:\n\n${labels}\n\nEnter number:`,
        '0',
      )
      if (raw === null) return
      const idx = parseInt(raw.trim(), 10)
      if (Number.isNaN(idx)) return
      reassignTo = idx === 0 ? null : (targets[idx - 1]?.id ?? null)
      if (idx > 0 && reassignTo === null) return
    } else {
      if (!window.confirm(`Delete empty category "${c.name}"?`)) return
    }
    void onDeleteCategory(c.id, reassignTo)
  }

  function applyBulkMove() {
    if (effectiveSelection.length === 0) return
    if (bulkCategory === '') return
    const targetID = bulkCategory === 'uncategorized' ? null : Number(bulkCategory)
    void onMoveSymbols(effectiveSelection, targetID)
    setSelected(new Set())
    setBulkCategory('')
  }

  function setSymbolCategory(symbol: string, categoryID: number | null) {
    void onMoveSymbols([symbol], categoryID)
  }

  function handleSave(event: FormEvent) {
    event.preventDefault()
    const targetID = addCategory === '' ? undefined : (addCategory === 'uncategorized' ? null : Number(addCategory))
    const symbols = splitSymbols(bulk)
    if (symbols.length === 0) return
    void onSave(targetID, event)
  }

  return (
    <section className="stack">
      <section className="stats-grid">
        <Metric label="Universe" value={fmtNum(items.length)} delta="managed symbols" tone="good" />
        <Metric label="Enabled" value={fmtNum(enabledCount)} delta="included in option crawls" tone={enabledCount ? 'good' : 'warn'} />
        <Metric label="Needs data" value={fmtNum(needsDataCount)} delta="enabled symbols waiting for first crawl" tone={needsDataCount ? 'warn' : 'good'} />
        <Metric label="UOA 7d" value={fmtNum(uoaAlertCount)} delta={fmtNum(uoaSymbolCount) + ' symbols with alerts'} />
      </section>

      <CrawlerTunables settings={crawlerSettings} symbolsEnabled={enabledCount} onSave={onSaveCrawlerSettings} />

      <section className="two-col">
        <Panel title="Add Symbols" kicker="Option Data">
          <form className="form-grid" onSubmit={handleSave}>
            <label className="field wide">
              <span>Symbols</span>
              <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} placeholder="AAPL, NVDA, SPY" spellCheck={false} />
            </label>
            <label className="field wide">
              <span>Category</span>
              <select value={addCategory} onChange={(e) => setAddCategory(e.target.value)}>
                <option value="">— keep existing —</option>
                <option value="uncategorized">Uncategorized</option>
                {sortedCategories.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            </label>
            <div className="actions wide">
              <button type="submit" disabled={splitSymbols(bulk).length === 0}>Save Symbols</button>
              <button type="button" className="ghost" onClick={onCrawlNow}>Crawl Now</button>
            </div>
          </form>
        </Panel>
        <Panel title="Coverage" kicker="Signals">
          <dl className="kv-list">
            <div><dt>Active set</dt><dd>{fmtNum(enabledCount)} enabled · {fmtNum(pausedCount)} paused</dd></div>
            <div><dt>Coverage</dt><dd>{fmtNum(coveredCount)} covered · {fmtNum(needsDataCount)} waiting for first crawl</dd></div>
            <div><dt>Uncategorized</dt><dd>{fmtNum(uncategorizedCount)} symbols not assigned to a category</dd></div>
            <div><dt>Collector behavior</dt><dd>continuous polling within Schwab rate cap; reload picks up symbol + category changes on the next pass</dd></div>
          </dl>
        </Panel>
      </section>

      <Panel title="Option Crawl Universe" kicker="Symbols" actions={(
        <form className="toolbar" onSubmit={onSearch}>
          <input value={query} onChange={(e) => setQuery(e.target.value)} type="search" placeholder="symbol or note" spellCheck={false} />
          <button type="submit">Search</button>
          <button type="button" className="ghost" onClick={handleCreateCategory}>+ New Category</button>
        </form>
      )}>
        <div className="category-strip" role="tablist" aria-label="Option universe views">
          {([
            { id: 'all', label: 'All', count: items.length },
            { id: 'enabled', label: 'Enabled', count: enabledCount },
            { id: 'disabled', label: 'Disabled', count: pausedCount },
            { id: 'uncategorized', label: 'Uncategorized', count: uncategorizedCount },
          ] as Array<{ id: 'all' | 'enabled' | 'disabled' | 'uncategorized'; label: string; count: number }>).map((sys) => {
            const v: OptionView = { kind: 'system', id: sys.id }
            const active = view.kind === 'system' && view.id === sys.id
            return (
              <button
                key={viewKey(v)}
                type="button"
                className={'category-chip ' + (active ? 'active' : '')}
                onClick={() => setView(v)}
                role="tab"
                aria-selected={active}
              >
                <span>{sys.label}</span>
                <strong>{fmtNum(sys.count)}</strong>
              </button>
            )
          })}
          {sortedCategories.map((c) => {
            const v: OptionView = { kind: 'category', id: c.id }
            const active = view.kind === 'category' && view.id === c.id
            return (
              <span key={viewKey(v)} className="category-chip-wrap" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <button
                  type="button"
                  className={'category-chip ' + (active ? 'active' : '')}
                  onClick={() => setView(v)}
                  role="tab"
                  aria-selected={active}
                  title={c.description || c.slug}
                >
                  <span>{c.name}{c.is_system ? ' ★' : ''}</span>
                  <strong>{fmtNum(c.symbol_count)}</strong>
                </button>
                {!c.is_system && (
                  <span className="actions" style={{ gap: '0.15rem' }}>
                    <button type="button" className="small ghost" title="Rename" onClick={() => handleRenameCategory(c)}>✎</button>
                    <button type="button" className="small danger" title="Delete" onClick={() => handleDeleteCategory(c)}>✕</button>
                  </span>
                )}
              </span>
            )
          })}
        </div>

        {effectiveSelection.length > 0 && (
          <div className="toolbar" style={{ marginTop: '0.5rem' }}>
            <strong>{effectiveSelection.length} selected</strong>
            <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)}>
              <option value="">Move to…</option>
              <option value="uncategorized">Uncategorized</option>
              {sortedCategories.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
            <button type="button" onClick={applyBulkMove} disabled={bulkCategory === ''}>Apply</button>
            <button type="button" className="ghost" onClick={() => setSelected(new Set())}>Clear selection</button>
          </div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: '2rem' }}>
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} aria-label="Select all visible" />
                </th>
                <th>Symbol</th>
                <th>Category</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Freshness</th>
                <th>UOA</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.length === 0 ? (
                <tr><td colSpan={8} className="empty-row">{items.length === 0 ? 'No symbols found.' : 'No symbols in this view.'}</td></tr>
              ) : visibleItems.map((item) => {
                const hasCoverage = Boolean(item.last_aggregate_at)
                const checked = selected.has(item.symbol)
                return (
                  <tr key={item.symbol}>
                    <td data-label="Select">
                      <input type="checkbox" checked={checked} onChange={() => toggleSelection(item.symbol)} aria-label={`Select ${item.symbol}`} />
                    </td>
                    <td data-label="Symbol">
                      <strong>{item.symbol}</strong>
                      <span className="pill">{item.source === 'admin' ? 'admin added' : 'default set'}</span>
                      {item.notes && <div className="mono">{item.notes}</div>}
                    </td>
                    <td data-label="Category">
                      <select
                        value={item.category_id ? String(item.category_id) : 'uncategorized'}
                        onChange={(e) => {
                          const v = e.target.value
                          setSymbolCategory(item.symbol, v === 'uncategorized' ? null : Number(v))
                        }}
                      >
                        <option value="uncategorized">— Uncategorized —</option>
                        {sortedCategories.map((c) => (
                          <option key={c.id} value={String(c.id)}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    <td data-label="Status">
                      <span className={'pill ' + (item.enabled ? 'pro' : 'warn')}>{item.enabled ? 'ENABLED' : 'PAUSED'}</span>
                      <div className="mono">updated {fmtDate(item.updated_at)}</div>
                    </td>
                    <td data-label="Priority"><span className="mono">{item.priority}</span></td>
                    <td data-label="Freshness">
                      {hasCoverage ? (
                        <div>{fmtDate(item.last_aggregate_at)}</div>
                      ) : (
                        <span className={'pill ' + (item.enabled ? 'warn' : '')}>{item.enabled ? 'Waiting for crawl' : 'Paused'}</span>
                      )}
                      <div className="mono">
                        {item.rows_30d > 0 ? `${fmtNum(item.rows_30d)} rows / 30d` : hasCoverage ? 'Coverage available' : item.enabled ? 'No coverage yet' : 'Not currently crawling'}
                      </div>
                    </td>
                    <td data-label="UOA">
                      <strong>{fmtNum(item.uoa_alerts_7d)}</strong>
                      <div className="mono">alerts / 7d</div>
                    </td>
                    <td className="right" data-label="Actions">
                      <div className="actions">
                        <button type="button" className="small ghost" onClick={() => onToggle(item, !item.enabled)}>
                          {item.enabled ? 'Pause' : 'Enable'}
                        </button>
                        <button type="button" className="small danger" onClick={() => onDelete(item.symbol)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </section>
  )
}

function CrawlerTunables({ settings, symbolsEnabled, onSave }: {
  settings: AdminCrawlerSettings | null
  symbolsEnabled: number
  onSave: (payload: Partial<Pick<AdminCrawlerSettings, 'rate_per_min' | 'burst' | 'workers' | 'min_pass_gap_sec' | 'pass_timeout_sec'>>) => void | Promise<void>
}) {
  const [draft, setDraft] = useState({
    rate_per_min: '',
    burst: '',
    workers: '',
    min_pass_gap_sec: '',
    pass_timeout_sec: '',
  })
  useEffect(() => {
    if (!settings) return
    setDraft({
      rate_per_min: String(settings.rate_per_min),
      burst: String(settings.burst),
      workers: String(settings.workers),
      min_pass_gap_sec: String(settings.min_pass_gap_sec),
      pass_timeout_sec: String(settings.pass_timeout_sec),
    })
  }, [settings])

  if (!settings) {
    return (
      <Panel title="Crawler Tunables" kicker="Schwab Option Crawl">
        <div className="empty-row">Loading crawler settings…</div>
      </Panel>
    )
  }

  const rate = Number(draft.rate_per_min) || 0
  const cap = settings.schwab_cap_per_min
  const overCap = rate > cap
  // Rough estimate: enabled symbols × (60 / rate_per_min) seconds, capped
  // by min_pass_gap_sec. Real-world will trend higher because Schwab
  // response time and parquet write also factor in.
  const estPassSec = rate > 0 ? Math.max(symbolsEnabled * (60 / rate), Number(draft.min_pass_gap_sec) || 0) : 0
  const passPerHour = estPassSec > 0 ? Math.floor(3600 / estPassSec) : 0

  const s = settings
  function submit(event: FormEvent) {
    event.preventDefault()
    const payload: Partial<Pick<AdminCrawlerSettings, 'rate_per_min' | 'burst' | 'workers' | 'min_pass_gap_sec' | 'pass_timeout_sec'>> = {}
    const parsedRate = Number(draft.rate_per_min)
    const parsedBurst = Number(draft.burst)
    const parsedWorkers = Number(draft.workers)
    const parsedGap = Number(draft.min_pass_gap_sec)
    const parsedTimeout = Number(draft.pass_timeout_sec)
    if (!Number.isNaN(parsedRate) && parsedRate !== s.rate_per_min) payload.rate_per_min = parsedRate
    if (!Number.isNaN(parsedBurst) && parsedBurst !== s.burst) payload.burst = parsedBurst
    if (!Number.isNaN(parsedWorkers) && parsedWorkers !== s.workers) payload.workers = parsedWorkers
    if (!Number.isNaN(parsedGap) && parsedGap !== s.min_pass_gap_sec) payload.min_pass_gap_sec = parsedGap
    if (!Number.isNaN(parsedTimeout) && parsedTimeout !== s.pass_timeout_sec) payload.pass_timeout_sec = parsedTimeout
    if (Object.keys(payload).length === 0) return
    void onSave(payload)
  }

  return (
    <Panel title="Crawler Tunables" kicker="Schwab Option Crawl" actions={
      <span className={'pill ' + (overCap ? 'warn' : '')}>Schwab cap: {cap} req/min</span>
    }>
      <form className="form-grid" onSubmit={submit}>
        <label className="field">
          <span>Rate (req/min)</span>
          <input type="number" min={1} max={cap} step={1}
            value={draft.rate_per_min}
            onChange={(e) => setDraft({ ...draft, rate_per_min: e.target.value })} />
        </label>
        <label className="field">
          <span>Burst</span>
          <input type="number" min={1} max={30} step={1}
            value={draft.burst}
            onChange={(e) => setDraft({ ...draft, burst: e.target.value })} />
        </label>
        <label className="field">
          <span>Workers</span>
          <input type="number" min={1} max={32} step={1}
            value={draft.workers}
            onChange={(e) => setDraft({ ...draft, workers: e.target.value })} />
        </label>
        <label className="field">
          <span>Min pass gap (sec)</span>
          <input type="number" min={0} max={3600} step={1}
            value={draft.min_pass_gap_sec}
            onChange={(e) => setDraft({ ...draft, min_pass_gap_sec: e.target.value })} />
        </label>
        <label className="field">
          <span>Pass timeout (sec)</span>
          <input type="number" min={30} max={3600} step={30}
            value={draft.pass_timeout_sec}
            onChange={(e) => setDraft({ ...draft, pass_timeout_sec: e.target.value })} />
        </label>
        <div className="actions wide">
          <button type="submit">Save &amp; Reload</button>
          {overCap && <span className="bad-text">⚠ rate exceeds Schwab cap</span>}
        </div>
      </form>
      <dl className="kv-list" style={{ marginTop: '0.75rem' }}>
        <div><dt>Estimated pass duration</dt><dd>~{estPassSec.toFixed(1)}s for {fmtNum(symbolsEnabled)} enabled symbols ({passPerHour}/hour during RTH)</dd></div>
        <div><dt>Last updated</dt><dd>{settings.updated_at ? fmtDate(settings.updated_at) : 'never'}</dd></div>
        <div><dt>Behavior</dt><dd>collector applies new values via pubsub reload — no restart needed</dd></div>
      </dl>
    </Panel>
  )
}

function UOAOps({ overview, config, onDetectNow }: {
  overview: AdminUOAOverview | null
  config: AdminConfig | null
  onDetectNow: () => void
}) {
  const pushEnabled = settingValue(config, 'push.enabled')
  return (
    <section className="stack">
      <section className="stats-grid">
        <Metric label="Alerts today" value={fmtNum(overview?.summary.alerts_today)} delta="US market date" tone={(overview?.summary.alerts_today ?? 0) > 0 ? 'good' : ''} />
        <Metric label="Alerts 7d" value={fmtNum(overview?.summary.alerts_7d)} delta="recent detections" />
      </section>
      <section className="two-col">
        <Panel title="Detector Control" kicker="UOA" actions={<button type="button" onClick={onDetectNow}>Detect Now</button>}>
          <dl className="kv-list">
            <div><dt>Latest trade date</dt><dd>{fmtDate(overview?.summary.latest_trade_date)}</dd></div>
            <div><dt>Latest detection</dt><dd>{fmtDate(overview?.summary.latest_detected_at)}</dd></div>
            <div><dt>Push status</dt><dd className={pushEnabled === false ? 'bad-text' : 'ok-text'}>{pushEnabled === false ? 'disabled' : 'enabled'}</dd></div>
          </dl>
        </Panel>
        <Panel title="Top Symbols" kicker="7 Days">
          {overview?.top_symbols.length ? (
            <ol className="rank-list">
              {overview.top_symbols.map((item) => (
                <li key={item.symbol}>
                  <strong>{item.symbol}</strong>
                  <span>{fmtNum(item.alerts_7d)} alerts</span>
                  <span>{item.max_multiple.toFixed(1)}x max</span>
                  <span>{fmtNum(item.total_volume)} vol</span>
                </li>
              ))}
            </ol>
          ) : <div className="empty-row">No UOA symbols in the last 7 days.</div>}
        </Panel>
      </section>
      <Panel title="Recent UOA Alerts" kicker="Detector">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Contract</th>
                <th>Volume vs Avg</th>
                <th>Multiple</th>
                <th>Description</th>
                <th>Detected</th>
              </tr>
            </thead>
            <tbody>
              {!overview?.recent_alerts.length ? (
                <tr><td colSpan={6} className="empty-row">No UOA detections found.</td></tr>
              ) : overview.recent_alerts.map((item) => (
                <tr key={[item.trade_date, item.detected_at, item.symbol, item.strike, item.side].join(':')}>
                  <td data-label="Symbol"><strong>{item.symbol}</strong><div className="mono">{fmtDate(item.trade_date)}</div></td>
                  <td data-label="Contract">{fmtDay(item.expiration)} · {typeof item.dte === 'number' ? item.dte + ' DTE · ' : ''}{item.strike} · {item.side}</td>
                  <td data-label="Volume vs Avg">
                    <strong>{fmtNum(item.today_volume)}</strong>
                    <div className="mono">7d avg {fmtCompactNumber(item.baseline_avg_7d ?? item.baseline_avg_30d)}</div>
                  </td>
                  <td data-label="Multiple"><span className="pill pro">{item.multiple.toFixed(1)}x</span></td>
                  <td data-label="Description">
                    {item.description
                      ? <div className="mono">{item.description}</div>
                      : <span className="muted">-</span>}
                  </td>
                  <td data-label="Detected">{fmtDate(item.detected_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </section>
  )
}

function Promotions({ trial, draft, setDraft, onSave }: {
  trial: SignupTrial | null
  draft: SignupTrialDraft
  setDraft: React.Dispatch<React.SetStateAction<SignupTrialDraft>>
  onSave: (event: FormEvent) => void
}) {
  return (
    <section className="two-col">
      <Panel title="Signup Trial" kicker="Growth Campaign" actions={<span className={'pill ' + (trial?.enabled ? 'pro' : '')}>{trial?.enabled ? 'ACTIVE' : 'OFF'}</span>}>
        <form className="form-grid" onSubmit={onSave}>
          <label className="field inline">
            <span>Enabled</span>
            <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft((prev) => ({ ...prev, enabled: e.target.checked }))} />
          </label>
          <label className="field">
            <span>Start</span>
            <input type="datetime-local" value={draft.startAt} onChange={(e) => setDraft((prev) => ({ ...prev, startAt: e.target.value }))} />
          </label>
          <label className="field">
            <span>End</span>
            <input type="datetime-local" value={draft.endAt} onChange={(e) => setDraft((prev) => ({ ...prev, endAt: e.target.value }))} />
          </label>
          <label className="field">
            <span>Free Pro days</span>
            <input type="number" min={1} max={365} value={draft.days} onChange={(e) => setDraft((prev) => ({ ...prev, days: e.target.value }))} />
          </label>
          <label className="field wide">
            <span>Product ID</span>
            <input value={draft.productID} onChange={(e) => setDraft((prev) => ({ ...prev, productID: e.target.value }))} spellCheck={false} />
          </label>
          <div className="actions wide">
            <button type="submit">Save Campaign</button>
          </div>
        </form>
      </Panel>
      <Panel title="Campaign Stats" kicker="Live">
        <TrialList trial={trial} />
      </Panel>
    </section>
  )
}

function PromoCodes({ codes, onCreate, onUpdate, onDelete }: {
  codes: AdminPromoCode[]
  onCreate: (payload: AdminPromoCodeCreate) => Promise<void>
  onUpdate: (code: string, payload: AdminPromoCodeUpdate) => Promise<void>
  onDelete: (code: string) => Promise<void>
}) {
  const [newCode, setNewCode] = useState('')
  const [newDays, setNewDays] = useState('30')
  const [newLimit, setNewLimit] = useState('')
  const [newExpires, setNewExpires] = useState('')
  const [newNotes, setNewNotes] = useState('')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const code = newCode.trim().toUpperCase()
    const days = Number(newDays)
    if (!code || !Number.isFinite(days) || days <= 0) return
    const payload: AdminPromoCodeCreate = {
      code,
      pro_days: Math.floor(days),
      enabled: true,
    }
    if (newLimit.trim()) {
      const cap = Number(newLimit)
      if (Number.isFinite(cap) && cap > 0) payload.max_redemptions = Math.floor(cap)
    }
    if (newExpires) {
      payload.expires_at = localDateTimeToISOString(newExpires)
    }
    if (newNotes.trim()) payload.notes = newNotes.trim()
    await onCreate(payload)
    setNewCode('')
    setNewDays('30')
    setNewLimit('')
    setNewExpires('')
    setNewNotes('')
  }

  return (
    <section className="two-col">
      <Panel title="Issue Promo Code" kicker="New code">
        <form className="form-grid" onSubmit={submit}>
          <label className="field">
            <span>Code</span>
            <input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              placeholder="LAUNCH30"
              spellCheck={false}
              autoCapitalize="characters"
            />
          </label>
          <label className="field">
            <span>Pro days granted</span>
            <input type="number" min={1} max={3650} value={newDays} onChange={(e) => setNewDays(e.target.value)} />
          </label>
          <label className="field">
            <span>Max redemptions (blank = unlimited)</span>
            <input type="number" min={1} value={newLimit} onChange={(e) => setNewLimit(e.target.value)} placeholder="unlimited" />
          </label>
          <label className="field">
            <span>Expires at (optional)</span>
            <input type="datetime-local" value={newExpires} onChange={(e) => setNewExpires(e.target.value)} />
          </label>
          <label className="field wide">
            <span>Internal notes</span>
            <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Why this code exists / who it's for" />
          </label>
          <div className="actions wide">
            <button type="submit" disabled={!newCode.trim() || !Number(newDays)}>Create code</button>
          </div>
        </form>
      </Panel>
      <Panel title="Promo Codes" kicker={`${codes.length} active`}>
        {codes.length === 0 ? (
          <p style={{ color: 'var(--dim, #888)', margin: 0 }}>No promo codes yet.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Pro days</th>
                  <th>Used / Cap</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {codes.map((p) => (
                  <PromoCodeRow key={p.code} promo={p} onUpdate={onUpdate} onDelete={onDelete} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </section>
  )
}

function PromoCodeRow({ promo, onUpdate, onDelete }: {
  promo: AdminPromoCode
  onUpdate: (code: string, payload: AdminPromoCodeUpdate) => Promise<void>
  onDelete: (code: string) => Promise<void>
}) {
  const used = promo.redemptions_count
  const cap = promo.max_redemptions
  const expiresLabel = promo.expires_at ? new Date(promo.expires_at).toLocaleString() : '—'

  return (
    <tr>
      <td className="mono">{promo.code}</td>
      <td>{promo.pro_days}</td>
      <td>{cap != null ? `${used} / ${cap}` : `${used}`}</td>
      <td>
        <span className={'pill ' + (promo.enabled ? 'pro' : '')}>
          {promo.enabled ? 'ENABLED' : 'DISABLED'}
        </span>
      </td>
      <td>{expiresLabel}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button type="button" onClick={() => onUpdate(promo.code, { enabled: !promo.enabled })}>
          {promo.enabled ? 'Disable' : 'Enable'}
        </button>{' '}
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`Delete promo code ${promo.code}? Redemption history (subscriptions) is preserved; the code becomes unredeemable.`)) {
              onDelete(promo.code)
            }
          }}
        >
          Delete
        </button>
      </td>
    </tr>
  )
}

function WebSubscriptionsLedger({ subscriptions, onOpenUser }: {
  subscriptions: AdminWebSubscription[]
  onOpenUser: (focus: UserFocus) => void
}) {
  const [sourceFilter, setSourceFilter] = useState<'' | 'stripe' | 'promo_code' | 'signup_trial' | 'admin'>('')
  const filtered = sourceFilter
    ? subscriptions.filter((s) => {
        if (sourceFilter === 'stripe') return s.environment.startsWith('stripe')
        return s.environment === sourceFilter
      })
    : subscriptions
  const activeCount = filtered.filter((s) => s.status === 'active').length
  const sourceLabel = (env: string) => {
    if (env.startsWith('stripe')) return `Stripe${env === 'stripe_test' ? ' (test)' : ''}`
    if (env === 'promo_code') return 'Promo code'
    if (env === 'signup_trial') return 'Signup trial'
    if (env === 'admin') return 'Admin grant'
    return env || '—'
  }
  return (
    <Panel
      title="Web & Promo Ledger"
      kicker={`Subscriptions · ${filtered.length}`}
      actions={(
        <div className="toolbar">
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as typeof sourceFilter)}>
            <option value="">All sources</option>
            <option value="stripe">Stripe</option>
            <option value="promo_code">Promo code</option>
            <option value="signup_trial">Signup trial</option>
            <option value="admin">Admin grant</option>
          </select>
          <span className="muted-line">{activeCount} active</span>
        </div>
      )}
    >
      <div className="callout">
        Non-StoreKit subscriptions: Stripe paid subs, redeemed promo codes, signup-trial promos, and admin-granted Pro. Shares search/status filter with the StoreKit ledger above.
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Source / Product</th>
              <th>User</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Last verified</th>
              <th className="right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="empty-row">No web subscriptions found.</td></tr>
            ) : filtered.map((s) => (
              <tr key={`${s.original_txn_id}:${s.user_id}:${s.last_verified_at}`}>
                <td data-label="Source / Product">
                  <strong>{sourceLabel(s.environment)}</strong>
                  <div className="mono muted-line">{s.product_id}</div>
                  <div className="mono">{s.original_txn_id}</div>
                </td>
                <td data-label="User">
                  <div>{s.user_email || s.user_id || '-'}</div>
                  <div className="mono">{s.auth_provider || '-'}</div>
                </td>
                <td data-label="Status">
                  <span className={'pill ' + (s.status === 'active' ? 'pro' : s.status === 'expired' ? 'warn' : '')}>
                    {s.status.toUpperCase()}
                  </span>
                  {s.cancel_at_period_end && s.status === 'active' && (
                    <div className="muted-line">cancels at period end</div>
                  )}
                </td>
                <td data-label="Expires">{s.expires_at ? new Date(s.expires_at).toLocaleString() : '-'}</td>
                <td data-label="Last verified">{s.last_verified_at ? new Date(s.last_verified_at).toLocaleString() : '-'}</td>
                <td data-label="Actions" className="right">
                  {s.user_id ? (
                    <button
                      type="button"
                      className="small"
                      onClick={() => onOpenUser({ userID: s.user_id, txnID: s.original_txn_id, source: 'web_subscription' })}
                    >
                      Open user
                    </button>
                  ) : (
                    <span className="muted-line">No user linked</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function Operations({ summary, config, health, events, onCrawlNow }: {
  summary: Summary | null
  config: AdminConfig | null
  health: CollectorHealth | null
  events: CollectorEvent[]
  onCrawlNow: () => void
}) {
  const pushEnabled = settingValue(config, 'push.enabled')
  const uoaMultiple = settingValue(config, 'uoa.detection.min_multiple')
  const uoaVolume = settingValue(config, 'uoa.detection.min_today_volume')
  return (
    <section className="stack">
      <section className="stats-grid">
        <Metric label="Events" value={fmtNum(summary?.events_24h)} delta="collector events 24h" tone="warn" />
        <Metric label="Push tokens" value={fmtNum(summary?.device_tokens)} delta={fmtNum(summary?.notification_messages_24h) + ' delivered records 24h'} tone={summary?.device_tokens ? 'good' : 'warn'} />
        <Metric label="Analyze runs" value={fmtNum(summary?.analyze_runs_24h)} delta="last 24h" />
        <Metric label="Positions" value={fmtNum(summary?.active_positions)} delta={fmtNum(summary?.pending_price_alerts) + ' pending alerts'} />
        <Metric label="Symbols" value={fmtNum(health?.last_scan_symbols)} delta="last scan" />
        <Metric label="Watchlist" value={fmtNum(summary?.watchlist_items)} delta="tracked symbols" tone="good" />
        <Metric label="StoreKit" value={fmtNum(summary?.storekit_transactions)} delta="transactions" />
      </section>
      <section className="two-col">
        <Panel title="Collector Control" kicker="Operations" actions={<button type="button" onClick={onCrawlNow}>Crawl Now</button>}>
          <HealthList health={health} />
        </Panel>
        <Panel title="Push & App Ops" kicker="Notifications">
          <dl className="kv-list">
            <div><dt>Global push</dt><dd className={pushEnabled === false ? 'bad-text' : 'ok-text'}>{pushEnabled === false ? 'disabled' : 'enabled'}</dd></div>
            <div><dt>Device tokens</dt><dd>{fmtNum(summary?.device_tokens)}</dd></div>
            <div><dt>Messages 24h</dt><dd>{fmtNum(summary?.notification_messages_24h)}</dd></div>
          </dl>
        </Panel>
        <Panel title="Analysis Runtime" kicker="LLM and UOA">
          <dl className="kv-list">
            <div><dt>Provider</dt><dd>{config?.llm.provider || '-'}</dd></div>
            <div><dt>Model</dt><dd>{config?.llm.model || '-'}</dd></div>
            <div><dt>API key</dt><dd className={config?.llm.api_key_configured ? 'ok-text' : 'bad-text'}>{config?.llm.api_key_configured ? 'configured' : 'missing'}</dd></div>
            <div><dt>UOA threshold</dt><dd>{String(uoaMultiple ?? '-')}x · {String(uoaVolume ?? '-')} vol</dd></div>
          </dl>
        </Panel>
        <Panel title="Collector Status" kicker="Runtime">
          <dl className="kv-list">
            <div><dt>Event storage</dt><dd>{events.length > 0 ? 'recording' : 'waiting for next collector write'}</dd></div>
            <div><dt>Last scan</dt><dd>{fmtDate(health?.last_scan_at)}</dd></div>
            <div><dt>Health updated</dt><dd>{fmtDate(health?.updated_at)}</dd></div>
            <div><dt>Degraded</dt><dd>{health?.degraded_until ? 'yes' : 'no'}</dd></div>
          </dl>
        </Panel>
      </section>
      <Panel title="Collector Events" kicker="Recent">
        <EventList events={events} emptyText="No collector events have been recorded yet. Use Crawl Now, then refresh after the collector finishes." />
      </Panel>
    </section>
  )
}

const metricsEventColumns: ProductEventName[] = [
  'scan_run',
  'scan_result_opened',
  'symbol_detail_opened',
  'analyze_started',
  'attention_item_opened',
  'position_reviewed',
  'roll_candidates_viewed',
]

function ProductMetrics({ weeks }: { weeks: AdminMetricsWeek[] }) {
  const latest = weeks[0]
  const northStar = latest && latest.active_users > 0
    ? (latest.qualified_sessions / latest.active_users).toFixed(2)
    : '—'
  const empty = weeks.every((week) => week.sessions === 0 && metricsEventColumns.every((event) => week.events[event] === 0))
  return (
    <Panel title="Weekly Product Events" kicker="Last 8 weeks">
      <div className="callout">
        North star: Weekly qualified decision sessions per active user · latest week {northStar}
      </div>
      {empty ? (
        <div className="empty-row">No product events yet.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Week</th>
                <th className="right">Active users</th>
                <th className="right">Qualified users</th>
                <th className="right">Qualified sessions</th>
                <th className="right">Sessions</th>
                {metricsEventColumns.map((event) => <th key={event} className="right">{event}</th>)}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week.week_start}>
                  <td data-label="Week"><strong>{week.week_start}</strong></td>
                  <td data-label="Active users" className="right">{fmtNum(week.active_users)}</td>
                  <td data-label="Qualified users" className="right">{fmtNum(week.qualified_users)}</td>
                  <td data-label="Qualified sessions" className="right">{fmtNum(week.qualified_sessions)}</td>
                  <td data-label="Sessions" className="right">{fmtNum(week.sessions)}</td>
                  {metricsEventColumns.map((event) => (
                    <td key={event} data-label={event} className="right">{fmtNum(week.events[event])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

function Audit({ events }: { events: AdminAuditEvent[] }) {
  return (
    <Panel title="Admin Audit Log" kicker="Governance">
      <ActivityList events={events} expanded />
    </Panel>
  )
}

function Panel({ title, kicker, actions, children }: { title: string; kicker: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <div className="eyebrow">{kicker}</div>
          <h2>{title}</h2>
        </div>
        {actions && <div className="panel-actions">{actions}</div>}
      </div>
      <div className="panel-body">{children}</div>
    </section>
  )
}

function RuntimeStrip({ summary, config, loading, sessionReady }: {
  summary: Summary | null
  config: AdminConfig | null
  loading: boolean
  sessionReady: boolean
}) {
  const health = summary?.collector_health ?? null
  const pushEnabled = settingValue(config, 'push.enabled')
  const collectorFresh = Boolean(health?.updated_at)
  const generated = summary?.generated_at ? fmtDate(summary.generated_at) : '-'
  return (
    <section className="runtime-strip" aria-label="Runtime state">
      <div>
        <span className={sessionReady ? 'dot ok' : 'dot'} />
        <strong>{loading ? 'Refreshing' : sessionReady ? 'Live session' : 'Signed out'}</strong>
      </div>
      <div>
        <span className={collectorFresh ? 'dot ok' : 'dot warn'} />
        <strong>Collector</strong>
        <span>{collectorFresh ? fmtDate(health?.updated_at) : 'waiting'}</span>
      </div>
      <div>
        <span className={pushEnabled === false ? 'dot warn' : 'dot ok'} />
        <strong>Push</strong>
        <span>{pushEnabled === false ? 'disabled' : 'enabled'}</span>
      </div>
      <div>
        <span className={config?.llm.api_key_configured ? 'dot ok' : 'dot warn'} />
        <strong>LLM</strong>
        <span>{config?.llm.model || '-'}</span>
      </div>
      <div>
        <strong>Snapshot</strong>
        <span>{generated}</span>
      </div>
    </section>
  )
}

function Metric({ label, value, delta, tone = '' }: { label: string; value: string; delta?: string; tone?: string }) {
  return (
    <article className={'metric ' + tone}>
      <span>{label}</span>
      <strong>{value}</strong>
      {delta && <small>{delta}</small>}
    </article>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="mini-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}


function DetailSection({ title, empty, children }: { title: string; empty: string; children: React.ReactNode }) {
  const count = React.Children.count(children)
  return (
    <div className="detail-section">
      <h3>{title}</h3>
      {count === 0 ? <div className="empty-row">{empty}</div> : <ol className="compact-list">{children}</ol>}
    </div>
  )
}

function HealthList({ health }: { health: CollectorHealth | null }) {
  return (
    <dl className="kv-list">
      <div><dt>Last scan</dt><dd>{fmtDate(health?.last_scan_at)}</dd></div>
      <div><dt>Symbols</dt><dd>{fmtNum(health?.last_scan_symbols)}</dd></div>
      <div><dt>Degraded until</dt><dd>{fmtDate(health?.degraded_until)}</dd></div>
      {health?.schwab_token_exp && <div><dt>Schwab token exp</dt><dd>{fmtDate(health.schwab_token_exp)}</dd></div>}
      <div><dt>Updated</dt><dd>{fmtDate(health?.updated_at)}</dd></div>
    </dl>
  )
}

function TrialList({ trial }: { trial: SignupTrial | null }) {
  return (
    <dl className="kv-list">
      <div><dt>Status</dt><dd className={trial?.enabled ? 'ok-text' : ''}>{trial?.enabled ? 'enabled' : 'disabled'}</dd></div>
      <div><dt>Window</dt><dd>{fmtDate(trial?.start_at)} to {fmtDate(trial?.end_at)}</dd></div>
      <div><dt>Days</dt><dd>{fmtNum(trial?.days)}</dd></div>
      <div><dt>Eligible users</dt><dd>{fmtNum(trial?.eligible_users)}</dd></div>
      <div><dt>Granted</dt><dd>{fmtNum(trial?.granted_users)}</dd></div>
      <div><dt>Active trials</dt><dd>{fmtNum(trial?.active_trial_users)}</dd></div>
    </dl>
  )
}

function buildSubscriptionFootprints(item: AdminSubscriptionLedgerItem): SubscriptionFootprint[] {
  const footprints = new Map<string, {
    installID: string
    users: Map<string, { id: string; label: string; authProvider?: string }>
    ips: Set<string>
    sources: Set<string>
    eventCount: number
    lastSeenAt?: string
  }>()

  const touch = (data: {
    installID?: string
    userID?: string
    userEmail?: string
    authProvider?: string
    ip?: string
    source?: string
    at?: string
  }) => {
    const installID = data.installID || ''
    const key = installID || ['no-install', data.userID || data.userEmail || data.ip || data.source || 'unknown'].join(':')
    const current = footprints.get(key) ?? {
      installID,
      users: new Map<string, { id: string; label: string; authProvider?: string }>(),
      ips: new Set<string>(),
      sources: new Set<string>(),
      eventCount: 0,
      lastSeenAt: undefined,
    }
    const userLabel = data.userEmail || data.userID || ''
    if (userLabel) {
      current.users.set(data.userID || userLabel, {
        id: data.userID || '',
        label: userLabel,
        authProvider: data.authProvider,
      })
    }
    if (data.ip) current.ips.add(data.ip)
    if (data.source) current.sources.add(labelAction(data.source))
    current.eventCount += 1
    if (isAfter(data.at, current.lastSeenAt)) current.lastSeenAt = data.at
    footprints.set(key, current)
  }

  if (item.current_user_id || item.current_user_email || item.current_app_install_id) {
    touch({
      installID: item.current_app_install_id,
      userID: item.current_user_id,
      userEmail: item.current_user_email,
      authProvider: item.current_auth_provider,
      source: 'current_link',
      at: item.last_seen_at,
    })
  }
  for (const event of item.restore_events ?? []) {
    touch({
      installID: event.app_install_id,
      userID: event.user_id,
      userEmail: event.user_email,
      authProvider: event.auth_provider,
      ip: event.client_ip,
      source: event.source,
      at: event.restored_at,
    })
  }

  return [...footprints.entries()]
    .map(([key, value]) => ({
      key,
      installID: value.installID,
      users: [...value.users.values()].sort((a, b) => a.label.localeCompare(b.label)),
      ips: [...value.ips].sort(),
      sources: [...value.sources].sort(),
      eventCount: value.eventCount,
      lastSeenAt: value.lastSeenAt,
    }))
    .sort((a, b) => {
      const bt = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0
      const at = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0
      return bt - at || a.installID.localeCompare(b.installID)
  })
}

function storeKitLedgerState(item: AdminSubscriptionLedgerItem): 'active' | 'expired' | 'revoked' {
  if (item.status === 'revoked') return 'revoked'
  const expires = item.expires_at ? new Date(item.expires_at) : null
  return expires && !Number.isNaN(expires.getTime()) && expires > new Date() ? 'active' : 'expired'
}

function isAfter(candidate: string | undefined, baseline: string | undefined): boolean {
  if (!candidate) return false
  if (!baseline) return true
  const candidateTime = new Date(candidate).getTime()
  const baselineTime = new Date(baseline).getTime()
  if (Number.isNaN(candidateTime)) return false
  if (Number.isNaN(baselineTime)) return true
  return candidateTime > baselineTime
}

function EventList({ events, emptyText = 'No collector events.' }: { events: CollectorEvent[]; emptyText?: string }) {
  if (events.length === 0) return <div className="empty-row">{emptyText}</div>
  return (
    <ol className="event-list">
      {events.map((item) => (
        <li key={item.id}>
          <div><strong>{item.kind}{item.symbol ? ' - ' + item.symbol : ''}</strong><span>{fmtDate(item.ts)}</span></div>
          <DetailViewer detail={item.detail} />
        </li>
      ))}
    </ol>
  )
}

function ActivityList({ events, expanded = false }: { events: AdminAuditEvent[]; expanded?: boolean }) {
  if (events.length === 0) return <div className="empty-row">No admin activity yet.</div>
  return (
    <ol className="event-list audit">
      {events.map((item) => (
        <li key={item.id}>
          <div><strong>{labelAction(item.action)}</strong><span>{fmtDate(item.ts)}</span></div>
          <p>{item.target}</p>
          {expanded && <DetailViewer detail={item.detail} />}
        </li>
      ))}
    </ol>
  )
}

const pages: PageMeta[] = [
  {
    id: 'overview', label: 'Overview', icon: '01', group: 'Command',
    title: 'Command Center', eyebrow: 'Live operations',
    description: 'A compact operating view for urgent actions, revenue health, runtime signals, and recent audit movement.',
  },
  {
    id: 'users', label: 'Users', icon: '02', group: 'Customers',
    title: 'Users', eyebrow: 'Accounts and entitlement',
    description: 'Find customer identities, inspect sessions and behavior, and grant or revoke entitlement from one place.',
  },
  {
    id: 'subscriptions', label: 'Subscriptions', icon: '03', group: 'Customers',
    title: 'Subscriptions', eyebrow: 'StoreKit ledger',
    description: 'Trace Apple original transactions across users, installs, restore events, and entitlement lifecycle.',
  },
  {
    id: 'options', label: 'Option Data', icon: '04', group: 'Data',
    title: 'Option Data', eyebrow: 'Crawl universe and coverage',
    description: 'Control the option crawl universe, review coverage freshness, and trigger manual collection when needed.',
  },
  {
    id: 'uoa', label: 'UOA Ops', icon: '05', group: 'Data',
    title: 'UOA Operations', eyebrow: 'Detector and fan-out',
    description: 'Monitor unusual-options detections, audience reach, recent pushes, and detector health.',
  },
  {
    id: 'growth', label: 'Growth', icon: '06', group: 'Customers',
    title: 'Growth', eyebrow: 'Promotions and trials',
    description: 'Configure signup trials and confirm live campaign eligibility, grants, and active trial counts.',
  },
  {
    id: 'settings', label: 'Settings', icon: '07', group: 'System',
    title: 'Settings', eyebrow: 'Stored configuration',
    description: 'Tune runtime limits, signal thresholds, push behavior, and multi-provider LLM routing with scoped saves.',
  },
  {
    id: 'operations', label: 'Operations', icon: '08', group: 'System',
    title: 'Operations', eyebrow: 'Collector and data flow',
    description: 'Watch collector health, app ops counters, LLM status, and recent runtime events.',
  },
  {
    id: 'audit', label: 'Audit', icon: '09', group: 'System',
    title: 'Audit', eyebrow: 'Admin activity',
    description: 'Review admin changes and operational actions with structured detail for follow-up.',
  },
  {
    id: 'metrics', label: 'Metrics', icon: '10', group: 'Command',
    title: 'Product Metrics', eyebrow: 'North star',
    description: 'Weekly product event counts, qualified decision sessions, and active-user reach over the last 8 weeks.',
  },
]

const navGroups: PageMeta['group'][] = ['Command', 'Customers', 'Data', 'System']

function pageMeta(page: Page): PageMeta {
  return pages.find((item) => item.id === page) ?? pages[0]
}

function makeTrialDraft(trial: SignupTrial | null): SignupTrialDraft {
  if (!trial) return defaultTrialDraft
  return {
    enabled: trial.enabled,
    startAt: isoToLocalDateTime(trial.start_at),
    endAt: isoToLocalDateTime(trial.end_at),
    days: String(trial.days || 30),
    productID: trial.product_id || 'promo.signup_trial',
  }
}

function makeUserDraft(user: AdminUser): UserDraft {
  const entitlement = describeEntitlement(user)
  return {
    email: user.email || '',
    status: entitlement.isTrial ? 'trial' : entitlement.isActive ? 'active' : '',
    productID: entitlement.isActive ? (user.product_id || defaultProductIDForStatus(entitlement.isTrial ? 'trial' : 'active', '')) : '',
    environment: entitlement.isTrial ? 'signup_trial' : entitlement.isActive ? (user.environment || 'admin') : '',
    expiresAt: isoToLocalDateTime(user.expires_at),
  }
}

function describeEntitlement(user: AdminUser): EntitlementView {
  const expires = user.expires_at ? new Date(user.expires_at) : null
  const hasFutureExpiry = Boolean(expires && !Number.isNaN(expires.getTime()) && expires > new Date())
  const isActive = user.subscription_status === 'active' && hasFutureExpiry
  const isTrial = isActive && user.environment === 'signup_trial'
  const state: EntitlementView['state'] = isActive ? (isTrial ? 'trial' : 'pro') : user.product_id ? 'expired' : 'free'
  return {
    isActive,
    isTrial,
    state,
    label: state === 'trial' ? 'TRIAL' : state === 'pro' ? 'PRO' : state === 'expired' ? 'EXPIRED' : 'FREE',
    productLabel: isActive ? productDisplayName(user.product_id, user.environment) : '-',
    productDetail: isActive ? productDetail(user.product_id) : inactiveProductDetail(user),
  }
}

function userActionState(user: AdminUser): UserActionView {
  const entitlement = describeEntitlement(user)
  const source = user.environment || ''
  const isManual = source === 'admin'
  const isTrial = source === 'signup_trial'
  const isStoreKit = entitlement.isActive && !isManual && !isTrial
  if (isStoreKit) {
    return {
      canGrant: false,
      grantLabel: '',
      canRevoke: false,
      revokeLabel: '',
      note: 'App Store managed',
    }
  }
  if (entitlement.isActive && isTrial) {
    return {
      canGrant: true,
      grantLabel: 'Grant Pro',
      canRevoke: true,
      revokeLabel: 'Revoke Trial',
      note: '',
    }
  }
  if (entitlement.isActive && isManual) {
    return {
      canGrant: true,
      grantLabel: 'Renew Grant',
      canRevoke: true,
      revokeLabel: 'Revoke Grant',
      note: '',
    }
  }
  return {
    canGrant: true,
    grantLabel: 'Grant Pro',
    canRevoke: false,
    revokeLabel: '',
    note: entitlement.state === 'expired' ? 'Expired' : '',
  }
}

function productDisplayName(productID?: string, environment?: string): string {
  if (!productID) return '-'
  switch (productID) {
    case 'admin.pro':
      return 'Manual Pro grant'
    case 'promo.signup_trial':
      return 'Signup trial'
    case 'com.navoption.app.pro.month':
      return 'App Store monthly'
    case 'com.navoption.app.pro.quarterly':
      return 'App Store 3-month'
    case 'com.navoption.app.pro.yearly':
      return 'App Store yearly'
    default:
      return environment === 'admin' ? 'Manual grant' : productID
  }
}

function productDetail(productID?: string): string {
  if (!productID) return ''
  return productID === 'admin.pro'
    ? 'internal id: admin.pro'
    : productID
}

function inactiveProductDetail(user: AdminUser): string {
  if (!user.product_id) return ''
  return 'inactive previous product: ' + user.product_id
}

function defaultProductIDForStatus(status: string, current: string): string {
  if (status === 'active') return current.trim() || 'admin.pro'
  if (status === 'trial') return current.trim() || 'promo.signup_trial'
  return ''
}

function userFocusFromLocation(): UserFocus | null {
  if (pageFromPath(window.location.pathname) !== 'users') return null
  return normalizeUserFocus({
    userID: new URLSearchParams(window.location.search).get('user') || undefined,
    installID: new URLSearchParams(window.location.search).get('install') || undefined,
    ip: new URLSearchParams(window.location.search).get('ip') || undefined,
    txnID: new URLSearchParams(window.location.search).get('txn') || undefined,
    source: new URLSearchParams(window.location.search).get('from') || undefined,
  })
}

function normalizeUserFocus(focus: UserFocus | null): UserFocus | null {
  if (!focus) return null
  const normalized: UserFocus = {
    userID: focus.userID?.trim() || undefined,
    installID: focus.installID?.trim() || undefined,
    ip: focus.ip?.trim() || undefined,
    txnID: focus.txnID?.trim() || undefined,
    source: focus.source?.trim() || undefined,
  }
  return normalized.userID || normalized.installID || normalized.ip || normalized.txnID || normalized.source
    ? normalized
    : null
}

function userFocusSearch(focus: UserFocus | null): URLSearchParams {
  const params = new URLSearchParams()
  const normalized = normalizeUserFocus(focus)
  if (!normalized) return params
  if (normalized.userID) params.set('user', normalized.userID)
  if (normalized.installID) params.set('install', normalized.installID)
  if (normalized.ip) params.set('ip', normalized.ip)
  if (normalized.txnID) params.set('txn', normalized.txnID)
  if (normalized.source) params.set('from', normalized.source)
  return params
}

function userFocusKey(focus: UserFocus | null): string {
  const normalized = normalizeUserFocus(focus)
  if (!normalized) return ''
  return [
    normalized.userID || '',
    normalized.installID || '',
    normalized.ip || '',
    normalized.txnID || '',
    normalized.source || '',
  ].join('|')
}

function userFocusLabel(focus: UserFocus): string {
  const parts = [
    focus.userID ? 'user ' + focus.userID : '',
    focus.installID ? 'install ' + focus.installID : '',
    focus.ip ? 'IP ' + focus.ip : '',
    focus.txnID ? 'txn ' + focus.txnID : '',
  ].filter(Boolean)
  return parts.join(' · ') || 'filtered user context'
}

function userFocusStatus(focus: UserFocus, openedUserID: string): string {
  if (openedUserID) return 'Opened user from subscription context: ' + userFocusLabel(focus)
  return 'Users filtered by subscription context: ' + userFocusLabel(focus)
}

function buildUserDeviceFootprints(detail: AdminUserDetail): UserDeviceFootprint[] {
  const groups = new Map<string, {
    installID: string
    ips: Set<string>
    platforms: Set<string>
    environments: Set<string>
    sources: Set<string>
    txnIDs: Set<string>
    tokenCount: number
    restoreCount: number
    lastSeenAt?: string
  }>()
  const ensure = (installID?: string, ip?: string, source?: string) => {
    const cleanInstall = installID || ''
    const key = cleanInstall || ['no-install', ip || source || 'unknown'].join(':')
    if (!groups.has(key)) {
      groups.set(key, {
        installID: cleanInstall,
        ips: new Set<string>(),
        platforms: new Set<string>(),
        environments: new Set<string>(),
        sources: new Set<string>(),
        txnIDs: new Set<string>(),
        tokenCount: 0,
        restoreCount: 0,
      })
    }
    return groups.get(key)!
  }

  if (detail.user.guest_install_id) {
    const group = ensure(detail.user.guest_install_id, undefined, 'identity')
    group.sources.add('identity')
    group.lastSeenAt = detail.user.last_seen_at || detail.user.created_at
  }
  ;(detail.device_tokens ?? []).forEach((item) => {
    const group = ensure(undefined, item.client_ip, 'push-token')
    if (item.client_ip) group.ips.add(item.client_ip)
    if (item.platform) group.platforms.add(item.platform)
    if (item.environment) group.environments.add(item.environment)
    group.sources.add('push-token')
    group.tokenCount += 1
    if (isAfter(item.last_seen_at, group.lastSeenAt)) group.lastSeenAt = item.last_seen_at
  })
  ;(detail.storekit_transactions ?? []).forEach((item) => {
    const group = ensure(item.app_install_id, undefined, 'storekit-current')
    if (item.environment) group.environments.add(item.environment)
    if (item.original_txn_id) group.txnIDs.add(item.original_txn_id)
    group.sources.add('storekit-current')
    if (isAfter(item.last_seen_at, group.lastSeenAt)) group.lastSeenAt = item.last_seen_at
  })
  ;(detail.storekit_restore_events ?? []).forEach((item) => {
    const group = ensure(item.app_install_id, item.client_ip, item.source || 'restore')
    if (item.client_ip) group.ips.add(item.client_ip)
    if (item.environment) group.environments.add(item.environment)
    if (item.original_txn_id) group.txnIDs.add(item.original_txn_id)
    if (item.source) group.sources.add(item.source)
    group.restoreCount += 1
    if (isAfter(item.restored_at, group.lastSeenAt)) group.lastSeenAt = item.restored_at
  })

  return [...groups.entries()].map(([key, value]) => ({
    key,
    installID: value.installID,
    ips: [...value.ips].sort(),
    platforms: [...value.platforms].sort(),
    environments: [...value.environments].sort(),
    sources: [...value.sources].sort(),
    txnIDs: [...value.txnIDs].sort(),
    tokenCount: value.tokenCount,
    restoreCount: value.restoreCount,
    lastSeenAt: value.lastSeenAt,
  })).sort((a, b) => {
    const bt = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0
    const at = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0
    return bt - at || a.installID.localeCompare(b.installID)
  })
}

function buildUserSubscriptionLinks(detail: AdminUserDetail): UserSubscriptionLink[] {
  const rows: UserSubscriptionLink[] = []
  ;(detail.storekit_transactions ?? []).forEach((item) => {
    rows.push({
      key: 'txn:' + item.original_txn_id,
      txnID: item.original_txn_id,
      product: productDisplayName(item.product_id, item.environment),
      status: item.status,
      installID: item.app_install_id,
      source: 'current transaction',
      expiresAt: item.expires_at,
      lastSeenAt: item.last_seen_at,
    })
  })
  ;(detail.storekit_restore_events ?? []).forEach((item) => {
    rows.push({
      key: 'restore:' + item.id,
      txnID: item.original_txn_id || '-',
      product: productDisplayName(item.product_id, item.environment),
      status: item.status,
      installID: item.app_install_id,
      ip: item.client_ip,
      source: item.source || 'restore',
      expiresAt: item.expires_at,
      lastSeenAt: item.restored_at,
    })
  })
  return rows.sort((a, b) => {
    const bt = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0
    const at = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0
    return bt - at
  }).slice(0, 20)
}

function matchesUserFocus(item: UserDeviceFootprint, focus: UserFocus | null): boolean {
  if (!focus) return false
  return Boolean(
    (focus.installID && item.installID === focus.installID) ||
    (focus.ip && item.ips.includes(focus.ip)) ||
    (focus.txnID && item.txnIDs.includes(focus.txnID)),
  )
}

function buildUserBehaviorEvents(detail: AdminUserDetail): UserBehaviorEvent[] {
  const events: UserBehaviorEvent[] = []
  ;(detail.storekit_restore_events ?? []).forEach((item) => {
    events.push({
      key: 'storekit:' + item.id,
      title: 'StoreKit ' + item.source,
      body: `${productDisplayName(item.product_id, item.environment)} · ${item.status} · IP ${item.client_ip || '-'}`,
      at: item.restored_at,
    })
  })
  ;(detail.device_tokens ?? []).forEach((item) => {
    events.push({
      key: 'device:' + item.token_preview + item.last_seen_at,
      title: 'Device token seen',
      body: `${item.platform} · ${item.environment} · IP ${item.client_ip || '-'}`,
      at: item.last_seen_at,
    })
  })
  ;(detail.notification_messages ?? []).forEach((item) => {
    events.push({
      key: 'notification:' + item.id,
      title: 'Notification sent',
      body: `${labelAction(item.kind)}${item.symbol ? ' · ' + item.symbol : ''}`,
      at: item.created_at,
    })
  })
  ;(detail.analyze_runs ?? []).forEach((item) => {
    events.push({
      key: 'analyze:' + item.id,
      title: 'Analyze run',
      body: `${item.symbol} · ${item.mode} · ${item.status}`,
      at: item.started_at,
    })
  })
  ;(detail.positions ?? []).forEach((item) => {
    events.push({
      key: 'position:' + item.id,
      title: 'Position opened',
      body: `${item.symbol} ${item.side} ${item.strike} · ${item.status}`,
      at: item.opened_at,
    })
  })
  ;(detail.price_alerts ?? []).forEach((item) => {
    events.push({
      key: 'price-alert:' + item.id,
      title: 'Price alert',
      body: `${item.symbol} ${item.side} ${item.target_price} · ${item.status}`,
      at: item.created_at,
    })
  })
  return events
    .filter((item) => item.at)
    .sort((a, b) => new Date(b.at as string).getTime() - new Date(a.at as string).getTime())
    .slice(0, 14)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function splitSymbols(raw: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  raw.split(/[\s,]+/).forEach((item) => {
    const symbol = item.trim().toUpperCase()
    if (!symbol || seen.has(symbol)) return
    seen.add(symbol)
    out.push(symbol)
  })
  return out
}

function buildActionItems(
  summary: Summary | null,
  config: AdminConfig | null,
  health: CollectorHealth | null,
  trial: SignupTrial | null,
  events: CollectorEvent[],
): ActionItem[] {
  const items: ActionItem[] = []
  if (settingValue(config, 'push.enabled') === false) {
    items.push({
      title: 'Push delivery is disabled',
      body: 'APNs fan-out is globally stopped. Re-enable only after the current incident is resolved.',
      page: 'settings',
      priority: 'high',
    })
  }
  if (config && !config.llm.api_key_configured) {
    items.push({
      title: 'LLM key is missing',
      body: 'Deep analysis can degrade or fail until the provider key is configured.',
      page: 'settings',
      priority: 'high',
    })
  }
  if (summary && summary.active_subscriptions > 0 && summary.device_tokens === 0) {
    items.push({
      title: 'No push devices registered',
      body: 'Pro users exist, but no APNs tokens are in the database. Check entitlement builds and notification permission onboarding.',
      page: 'users',
      priority: 'high',
    })
  }
  if (!health?.updated_at) {
    items.push({
      title: 'Collector health has not reported',
      body: 'The runtime health row is empty, so scans and stream status cannot be trusted yet.',
      page: 'operations',
      priority: 'high',
    })
  } else if (health.degraded_until && new Date(health.degraded_until) > new Date()) {
    items.push({
      title: 'Collector is degraded',
      body: 'A recent crawl failure marked the collector degraded. Inspect operations before relying on scan freshness.',
      page: 'operations',
      priority: 'high',
    })
  }
  if (events.length === 0) {
    items.push({
      title: 'Collector event log is empty',
      body: 'Collector runtime writes should appear here after restart or crawl. Trigger a crawl if you need fresh evidence.',
      page: 'operations',
      priority: 'medium',
    })
  }
  if (trial && !trial.enabled) {
    items.push({
      title: 'Signup trial is disabled',
      body: 'Growth campaign rules exist but are not active for new registrations.',
      page: 'growth',
      priority: 'medium',
    })
  }
  if (summary && summary.users === 0) {
    items.push({
      title: 'No users yet',
      body: 'The admin is live, but no customer accounts have been recorded.',
      page: 'users',
      priority: 'low',
    })
  }
  return items
}

function settingValue(config: AdminConfig | null, key: string): unknown {
  return config?.settings.find((item) => item.key === key)?.value
}

function fmtDate(value?: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString()
}

function fmtDay(value?: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value.slice(0, 10)
  return date.toISOString().slice(0, 10)
}

function fmtNum(value?: number | null): string {
  if (value === undefined || value === null) return '-'
  return Number(value).toLocaleString()
}

function fmtCompactNumber(value?: number | string | null): string {
  if (value === undefined || value === null || value === '') return '-'
  const number = typeof value === 'number' ? value : Number.parseFloat(value)
  if (Number.isNaN(number)) return String(value)
  const digits = number >= 100 ? 0 : number >= 10 ? 1 : 2
  return number.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })
}

function isoToLocalDateTime(value?: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset()
  const local = new Date(date.getTime() - offset * 60_000)
  return local.toISOString().slice(0, 16)
}

function localDateTimeToISOString(value: string): string | undefined {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toISOString()
}

function creationOptionsFromJSON(payload: unknown): PublicKeyCredentialCreationOptions {
  const input = publicKeyPayload(payload) as Record<string, any>
  return {
    ...input,
    challenge: base64URLToBuffer(input.challenge),
    user: {
      ...input.user,
      id: base64URLToBuffer(input.user.id),
    },
    excludeCredentials: input.excludeCredentials?.map(credentialDescriptorFromJSON),
  } as PublicKeyCredentialCreationOptions
}

function requestOptionsFromJSON(payload: unknown): PublicKeyCredentialRequestOptions {
  const input = publicKeyPayload(payload) as Record<string, any>
  return {
    ...input,
    challenge: base64URLToBuffer(input.challenge),
    allowCredentials: input.allowCredentials?.map(credentialDescriptorFromJSON),
  } as PublicKeyCredentialRequestOptions
}

function credentialDescriptorFromJSON(input: Record<string, any>): PublicKeyCredentialDescriptor {
  return {
    ...input,
    type: input.type ?? 'public-key',
    id: base64URLToBuffer(input.id),
    transports: input.transports as AuthenticatorTransport[] | undefined,
  }
}

function publicKeyPayload(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') throw new Error('Invalid passkey options')
  const maybeWrapped = payload as { publicKey?: unknown }
  return maybeWrapped.publicKey ?? payload
}

function publicKeyCredentialToJSON(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response
  const base = {
    id: credential.id,
    rawId: bufferToBase64URL(credential.rawId),
    type: credential.type,
    authenticatorAttachment: (credential as PublicKeyCredential & { authenticatorAttachment?: string | null }).authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults(),
  }
  if (response instanceof AuthenticatorAttestationResponse) {
    return {
      ...base,
      response: {
        clientDataJSON: bufferToBase64URL(response.clientDataJSON),
        attestationObject: bufferToBase64URL(response.attestationObject),
        transports: response.getTransports?.() ?? [],
      },
    }
  }
  const assertion = response as AuthenticatorAssertionResponse
  return {
    ...base,
    response: {
      clientDataJSON: bufferToBase64URL(assertion.clientDataJSON),
      authenticatorData: bufferToBase64URL(assertion.authenticatorData),
      signature: bufferToBase64URL(assertion.signature),
      userHandle: assertion.userHandle ? bufferToBase64URL(assertion.userHandle) : null,
    },
  }
}

function base64URLToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const binary = window.atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function bufferToBase64URL(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function prettyJSON(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2)
}

function StructuredValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (value === null || value === undefined) return <span className="sv-empty">—</span>
  if (typeof value === 'boolean') {
    return <span className={value ? 'sv-bool on' : 'sv-bool off'}>{value ? 'true' : 'false'}</span>
  }
  if (typeof value === 'number' || typeof value === 'bigint') {
    return <span className="sv-num">{String(value)}</span>
  }
  if (typeof value === 'string') {
    if (value === '') return <span className="sv-empty">empty</span>
    return <span className="sv-str">{value}</span>
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="sv-empty">empty list</span>
    const allPrim = value.every(
      (v) => v === null || ['string', 'number', 'boolean', 'bigint'].includes(typeof v),
    )
    if (allPrim) {
      return (
        <div className="sv-chips">
          {value.map((v, i) => (
            <span key={i} className="sv-chip">{v === null ? '—' : String(v)}</span>
          ))}
        </div>
      )
    }
    return (
      <ol className="sv-list">
        {value.map((v, i) => (
          <li key={i}><StructuredValue value={v} depth={depth + 1} /></li>
        ))}
      </ol>
    )
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) return <span className="sv-empty">empty</span>
    return (
      <dl className={`sv-kv sv-depth-${Math.min(depth, 3)}`}>
        {entries.map(([k, v]) => (
          <div className="sv-row" key={k}>
            <dt>{k}</dt>
            <dd><StructuredValue value={v} depth={depth + 1} /></dd>
          </div>
        ))}
      </dl>
    )
  }
  return <span className="sv-str">{String(value)}</span>
}

function DetailViewer({ detail }: { detail: unknown }) {
  const [raw, setRaw] = useState(false)
  const isEmpty =
    detail === null ||
    detail === undefined ||
    (typeof detail === 'object' && !Array.isArray(detail) && Object.keys(detail as object).length === 0)
  if (isEmpty) return null
  return (
    <div className="detail-viewer">
      <button
        type="button"
        className="detail-viewer-toggle"
        onClick={() => setRaw((value) => !value)}
      >
        {raw ? 'Show structured' : 'Show raw JSON'}
      </button>
      {raw ? <pre className="sv-raw">{prettyJSON(detail)}</pre> : <StructuredValue value={detail} />}
    </div>
  )
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function isSessionError(message: string): boolean {
  const normalized = message.toLowerCase()
  return normalized.includes('unauthorized') || normalized.includes('session required')
}

function titleCase(value: string): string {
  return value.replace(/(^|[-_ ])\w/g, (match) => match.toUpperCase()).replace(/[-_]/g, ' ')
}

function labelAction(value: string): string {
  return titleCase(value.replace(/_/g, ' '))
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
